/**
 * Traffic: cars driving the real roads around the player.
 *
 * Every vector chunk contributes its bigger roads as lanes (see
 * `core/traffic/lanes.ts`). As with the villagers, a population is kept alive
 * within a radius of the player: spawned on lanes out toward the edge of it,
 * retired once far behind. How many follows how much road there is nearby
 * and how busy each kind of road is, so a motorway interchange gets a stream
 * of cars and a country road the odd one.
 *
 * Each car follows its lane at the road's speed, slowing for bends, and keeps
 * its distance by the Intelligent Driver Model (`idmAcceleration`) from
 * whatever is ahead of it in its lane: another car, or the player. Some way
 * before a lane ends it picks where it will carry on, slows for the turn,
 * and when that takes it onto a bigger road, stops at the line until nothing
 * is coming. On a road with more than one lane each way, cars keep to the
 * right-hand lane and move out to the left only to pass someone slower, then
 * back in once there is room (`changeLanes`). Cars are solid: the player is pushed out of them as out of a
 * parked car (`collide`).
 *
 * Cars are drawn as parked cars are, the drivable model baked flat in one
 * instanced mesh per kind, but without their wheels: those are two more
 * instanced meshes, one for each side, placed every frame to roll with the
 * car's speed and steer by how fast it is turning. Seventy cars are eight
 * draw calls.
 */

import type { InstancedMesh, Object3D } from 'three';
import { Color, Euler, Group, Matrix4, Quaternion, Vector3 } from 'three';
import { makeRng } from '../core/build/rng.js';
import type { VehicleSpecies } from '../core/build/parking.js';
import { SEMI_REACH, VEHICLE_SIZE, pickCar, pickPaint } from '../core/build/parking.js';
import type { Lane, LaneSample } from '../core/traffic/lanes.js';
import { ROAD_STYLE } from '../core/sources/shortbread.js';
import { CAR_DRIVER, TRUCK_DRIVER, comingThrough, idmAcceleration, makeTurn, projectOnLane, sampleLane, sampleTurn, turnSpeedLimit } from '../core/traffic/lanes.js';
import type { LaneProjection, TurnCurve } from '../core/traffic/lanes.js';
import { followTrail, shiftTrail } from '../core/traffic/trailer.js';
import type { Footprint } from '../core/vehicle/collision.js';
import { STIFFNESS, clearance, conflict, crushSpeeds, firstOnPath, massOf, overlap, yields } from '../core/vehicle/collision.js';
import { KNOCK_KMH } from '../core/vehicle/damage.js';
import type { Hit } from '../core/character/struck.js';
import { KNOCKED_DOWN, strike } from '../core/character/struck.js';
import type { PersonReading, StreetPerson, Temperament } from '../core/traffic/pedestrians.js';
import { CLOSE_BY, PASSING_SPEED, PERSON_BRAKE, approachSpeed, bodyClearance, canYield, pickTemperament, readPerson } from '../core/traffic/pedestrians.js';
import type { Livery } from '../core/traffic/police.js';
import { PULL_OVER, RESPONDING_OVER, RESPONDING_SHARE, YIELD_SPEED, givesWay, liveryOf, policeShare } from '../core/traffic/police.js';
import type { TrafficModel } from './flora.js';
import { CarLights, lampLayout, type LampLayout } from './carLights.js';
import { LIVERY, floraNight, liveried, movingInstances, trafficModel, trafficMotorbike, trafficWheels } from './flora.js';
import type { SirenSource } from './police.js';
import { PoliceLights } from './police.js';
import type { CarKind } from './Vehicle.js';
import { roofTop } from './Vehicle.js';
import type { BusLook, TransitTrip, TripLayout } from '../core/transit/trip.js';
import { pointAlong, scheduledAt } from '../core/transit/trip.js';
import type { BusService } from '../core/transit/service.js';
import { serve, startService, toStop } from '../core/transit/service.js';
import { VT_BLUE } from './Bus.js';

interface ChunkLanes {
  group: Object3D;
  lanes: Lane[];
  /** Local-frame bounds of all its lanes: minX, minZ, maxX, maxZ. */
  bounds: [number, number, number, number];
  /** Each lane's own bounds, four to a lane: so a search near a point skips the rest without looking along them. */
  laneBounds: Float32Array;
}

/** Whether lane `i`'s bounds come within `reach` of (x, z). */
function laneNear(chunk: ChunkLanes, i: number, x: number, z: number, reach: number): boolean {
  const b = chunk.laneBounds;
  return x >= b[i * 4] - reach && x <= b[i * 4 + 2] + reach && z >= b[i * 4 + 1] - reach && z <= b[i * 4 + 3] + reach;
}

export interface LaneSpot {
  chunk: string;
  lane: number;
  distance: number;
}

/** Every traffic lane currently streamed in, keyed by vector chunk. */
export class LaneNetwork {
  private readonly chunks = new Map<string, ChunkLanes>();

  add(id: string, group: Object3D, lanes: Lane[]): void {
    if (lanes.length === 0) {
      this.chunks.delete(id);
      return;
    }
    const bounds: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity];
    for (const lane of lanes) {
      for (let i = 0; i < lane.points.length; i += 3) {
        bounds[0] = Math.min(bounds[0], lane.points[i]);
        bounds[1] = Math.min(bounds[1], lane.points[i + 2]);
        bounds[2] = Math.max(bounds[2], lane.points[i]);
        bounds[3] = Math.max(bounds[3], lane.points[i + 2]);
      }
    }
    const laneBounds = new Float32Array(lanes.length * 4);
    lanes.forEach((lane, i) => {
      let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
      for (let k = 0; k < lane.points.length; k += 3) {
        minX = Math.min(minX, lane.points[k]); maxX = Math.max(maxX, lane.points[k]);
        minZ = Math.min(minZ, lane.points[k + 2]); maxZ = Math.max(maxZ, lane.points[k + 2]);
      }
      laneBounds.set([minX, minZ, maxX, maxZ], i * 4);
    });
    this.chunks.set(id, { group, lanes, bounds, laneBounds });
  }

  remove(id: string): void {
    this.chunks.delete(id);
  }

  get(id: string): ChunkLanes | undefined {
    return this.chunks.get(id);
  }

  /** Chunks with a lane within `radius` of scene (x, z). */
  private *near(x: number, z: number, radius: number): Generator<[string, ChunkLanes, number, number]> {
    for (const [id, chunk] of this.chunks) {
      const lx = x - chunk.group.position.x;
      const lz = z - chunk.group.position.z;
      const [minX, minZ, maxX, maxZ] = chunk.bounds;
      if (lx < minX - radius || lx > maxX + radius || lz < minZ - radius || lz > maxZ + radius) continue;
      yield [id, chunk, lx, lz];
    }
  }

  /**
   * How many cars the roads within `radius` of scene (x, z) carry: each
   * lane's metres in range by its cars per kilometre.
   */
  carsNear(x: number, z: number, radius: number): number {
    let sum = 0;
    for (const [, chunk, lx, lz] of this.near(x, z, radius)) {
      for (const lane of chunk.lanes) {
        for (let i = 1; i < lane.distances.length; i++) {
          if (Math.hypot(lane.points[i * 3] - lx, lane.points[i * 3 + 2] - lz) > radius) continue;
          sum += ((lane.distances[i] - lane.distances[i - 1]) * lane.density) / 1000;
        }
      }
    }
    return sum;
  }

  /** A random spot between `minRadius` and `maxRadius` of scene (x, z), busier roads likelier. */
  pick(x: number, z: number, minRadius: number, maxRadius: number, rng: () => number): LaneSpot | undefined {
    const candidates: LaneSpot[] = [];
    const weights: number[] = [];
    let total = 0;
    for (const [id, chunk, lx, lz] of this.near(x, z, maxRadius)) {
      chunk.lanes.forEach((lane, index) => {
        for (let i = 0; i < lane.distances.length - 1; i += 3) {
          const d = Math.hypot(lane.points[i * 3] - lx, lane.points[i * 3 + 2] - lz);
          if (d >= minRadius && d <= maxRadius) {
            candidates.push({ chunk: id, lane: index, distance: lane.distances[i] });
            total += lane.density;
            weights.push(total);
          }
        }
      });
    }
    if (candidates.length === 0) return undefined;
    const u = rng() * total;
    let lo = 0, hi = weights.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (weights[mid] < u) lo = mid + 1;
      else hi = mid;
    }
    return candidates[lo];
  }

  /**
   * Where a car at scene (x, z) heading (dx, dz), at the end of its lane,
   * carries on: a lane that starts there or passes close by, straight on
   * most likely, turning left or right less so. At a dead end, where there is
   * nothing else, it turns round onto the other side of its road. Without
   * `from`, any lane at all: for finding a car's road again once its tile
   * has been rebuilt.
   *
   * Going round a roundabout, the ring itself is always the straightest way
   * on, so `leave` says how keen the car is to get off it: 'no' just after
   * coming on, 'maybe' an even chance at each exit, 'must' at the next one.
   */
  next(
    x: number, z: number, dx: number, dz: number, from: LaneSpot | undefined, rng: () => number,
    leave?: 'no' | 'maybe' | 'must',
    /** Somewhere to head for, scene x, z: the one a police car chases. The way toward it is taken, turning round if need be. */
    toward?: { x: number; z: number },
  ): LaneSpot | undefined {
    const reach = 6;
    const options: Array<{ spot: LaneSpot; weight: number; ring: boolean }> = [];
    const back: Array<{ spot: LaneSpot; weight: number; ring: boolean }> = [];
    for (const [id, chunk, lx, lz] of this.near(x, z, reach)) {
      chunk.lanes.forEach((lane, index) => {
        if (from !== undefined && id === from.chunk && index === from.lane) return;
        if (!laneNear(chunk, index, lx, lz, reach)) return;
        const at = projectOnLane(lane, lx, lz, reach, projection);
        if (at === undefined || lane.length - at.distance < 5) return;
        const along = at.dx * dx + at.dz * dz;
        // Finding a car's own road again, only one running its way will do.
        if (from === undefined && along < 0.8) return;
        const fromStart = at.distance < 12;
        const spot = { chunk: id, lane: index, distance: at.distance };
        // Most of all, the lane that lines up with the one it leaves: the
        // same lane on, not its neighbour a lane's width across.
        const near = (1 - at.off / (reach + 1)) ** 6;
        // Chasing, the way that points at the one chased, by far.
        let keen = 1;
        if (toward !== undefined) {
          const tx = toward.x - x, tz = toward.z - z, t = Math.hypot(tx, tz) || 1;
          keen = (1 + 4 * Math.max(0, (at.dx * tx + at.dz * tz) / t)) ** 4;
        }
        if (along < -0.5) (toward !== undefined && keen > 20 ? options : back).push({ spot, weight: near * keen, ring: lane.roundabout });
        else options.push({ spot, weight: (along + 1) ** 2 * (fromStart ? 4 : 1) * near * keen, ring: lane.roundabout });
      });
    }
    if (options.length === 0 && from !== undefined) options.push(...back);
    if (leave !== undefined) {
      const round = options.filter(o => o.ring), off = options.filter(o => !o.ring);
      const out = off.length > 0 && (leave === 'must' || round.length === 0 || (leave === 'maybe' && rng() < 0.5));
      const chosen = out ? off : round.length > 0 ? round : off;
      options.length = 0;
      options.push(...chosen);
    }
    if (options.length === 0) return undefined;
    let u = rng() * options.reduce((s, o) => s + o.weight, 0);
    for (const option of options) {
      u -= option.weight;
      if (u <= 0) return option.spot;
    }
    return options[options.length - 1].spot;
  }

  /**
   * The middle of the lane to drive in near scene (x, z), going (dx, dz): of
   * the lanes that way there, the rightmost, or with `left` the one beside it.
   * Whether there is a lane to its left. For the autopilot, so it drives in a
   * lane rather than at a guess right of the road's line.
   */
  laneFor(x: number, z: number, dx: number, dz: number, left: boolean): { x: number; z: number; hasLeft: boolean } | undefined {
    let best: { chunk: ChunkLanes; lane: Lane } | undefined, bestOff = 7;
    for (const [, chunk, lx, lz] of this.near(x, z, 7)) {
      for (let index = 0; index < chunk.lanes.length; index++) {
        if (!laneNear(chunk, index, lx, lz, bestOff)) continue;
        const lane = chunk.lanes[index];
        const at = projectOnLane(lane, lx, lz, bestOff, projection);
        if (at === undefined || at.dx * dx + at.dz * dz < 0.7) continue;
        bestOff = at.off;
        best = { chunk, lane };
      }
    }
    if (best === undefined) return undefined;
    // Over to the rightmost lane of its carriageway, then out one if asked.
    let lane = best.lane;
    while (lane.right >= 0) lane = best.chunk.lanes[lane.right];
    const hasLeft = lane.left >= 0;
    if (left && hasLeft) lane = best.chunk.lanes[lane.left];
    const ox = best.chunk.group.position.x, oz = best.chunk.group.position.z;
    const at = projectOnLane(lane, x - ox, z - oz, 12, projection);
    if (at === undefined) return undefined;
    sampleLane(lane, at.distance, ahead);
    return { x: ahead.x + ox, z: ahead.z + oz, hasLeft };
  }

  /** The road's speed, m/s, for someone at scene (x, z) going (dx, dz): the lane they are on, that way; undefined off the road. */
  limitAt(x: number, z: number, dx: number, dz: number): number | undefined {
    let best: Lane | undefined, bestOff = 4;
    for (const [, chunk, lx, lz] of this.near(x, z, 4)) {
      for (let index = 0; index < chunk.lanes.length; index++) {
        if (!laneNear(chunk, index, lx, lz, bestOff)) continue;
        const lane = chunk.lanes[index];
        const at = projectOnLane(lane, lx, lz, bestOff, projection);
        if (at === undefined || at.dx * dx + at.dz * dz < 0.7) continue;
        bestOff = at.off;
        best = lane;
      }
    }
    return best?.speed;
  }

  /** The nearest point of any lane within `reach` of scene (x, z), and which way the lane runs there. */
  nearestLane(x: number, z: number, reach: number): { x: number; z: number; dx: number; dz: number } | undefined {
    let best: { x: number; z: number; dx: number; dz: number } | undefined, bestOff = reach;
    for (const [, chunk, lx, lz] of this.near(x, z, reach)) {
      for (let index = 0; index < chunk.lanes.length; index++) {
        if (!laneNear(chunk, index, lx, lz, bestOff)) continue;
        const lane = chunk.lanes[index];
        const at = projectOnLane(lane, lx, lz, bestOff, projection);
        if (at === undefined || at.off >= bestOff) continue;
        bestOff = at.off;
        sampleLane(lane, at.distance, ahead);
        best = { x: ahead.x + chunk.group.position.x, z: ahead.z + chunk.group.position.z, dx: ahead.dx, dz: ahead.dz };
      }
    }
    return best;
  }

  /**
   * Whether a lane runs the other way beside scene (x, z), going (dx, dz):
   * one about a lane's width to its left, heading back. A two-way road has
   * one; a one-way street or a dual carriageway's own side does not.
   */
  hasOncoming(x: number, z: number, dx: number, dz: number): boolean {
    const ox = x + dz * 3.5, oz = z - dx * 3.5;
    for (const [, chunk, lx, lz] of this.near(ox, oz, 3)) {
      for (let index = 0; index < chunk.lanes.length; index++) {
        if (!laneNear(chunk, index, lx, lz, 2.5)) continue;
        const at = projectOnLane(chunk.lanes[index], lx, lz, 2.5, projection);
        if (at !== undefined && at.dx * dx + at.dz * dz < -0.7) return true;
      }
    }
    return false;
  }

  /**
   * A road leaving a roundabout's ring near scene (x, z), where the ring runs
   * (dx, dz): a lane off the ring that starts within a few metres, going
   * roughly the ring's way or out to its right.
   */
  exitNear(x: number, z: number, dx: number, dz: number): LaneSpot | undefined {
    const reach = 7;
    let found: LaneSpot | undefined, foundD = reach;
    for (const [id, chunk, lx, lz] of this.near(x, z, reach)) {
      chunk.lanes.forEach((lane, index) => {
        if (lane.roundabout || !laneNear(chunk, index, lx, lz, foundD)) return;
        const at = projectOnLane(lane, lx, lz, foundD, projection);
        if (at === undefined || at.distance > 12 || at.dx * dx + at.dz * dz < -0.3) return;
        foundD = at.off;
        found = { chunk: id, lane: index, distance: at.distance };
      });
    }
    return found;
  }
}

/** A way off a roundabout's ring: where along the ring lane to leave it, and onto what. */
interface RingExit {
  at: number;
  spot: LaneSpot;
}

interface Car {
  /** The exit it will take off the roundabout it is going round, once chosen. */
  exit: RingExit | undefined;
  /** Exits it has already passed up on this time round, by lane. */
  passed: Set<string>;
  species: VehicleSpecies;
  paint: number;
  spot: LaneSpot;
  speed: number;
  /** This driver's share of the road's speed. */
  temper: number;
  /** Logical position and heading, scene metres. */
  x: number;
  y: number;
  z: number;
  dx: number;
  dz: number;
  grade: number;
  /** Where it is drawn relative to the logical position, glided away after a change of lane. */
  offsetX: number;
  offsetZ: number;
  offsetYaw: number;
  yaw: number;
  /** Seconds since its lane went away with a tile being rebuilt; 0 while on one. */
  adrift: number;
  /** Where it will carry on at the end of this lane: undefined until chosen, null where nowhere. */
  next: LaneSpot | null | undefined;
  /** Seconds spent waiting to pull out at this junction. */
  waited: number;
  /** Seconds standing still; past `STUCK` it stops waiting on other cars for a while. */
  stuck: number;
  id: number;
  /** Seconds since it last changed lanes. */
  settled: number;
  /** On a roundabout's ring, where it has right of way. */
  ring: boolean;
  /**
   * An articulated truck's trailer: the track its kingpin has driven, which
   * the axles follow (see core/traffic/trailer.ts), where the kingpin was
   * last frame, and which way the trailer points, forward. Undefined for
   * anything else.
   */
  trailer?: { trail: number[]; kx: number; kz: number; dx: number; dz: number };
  /**
   * A turn under way from the end of one lane onto another, in the new
   * lane's chunk's frame: how far along it the car is, and where on the new
   * lane it comes out (also `spot.distance` meanwhile).
   */
  turn: { curve: TurnCurve; s: number; end: number } | undefined;
  /** The turn it will make onto `next`, worked out ahead for seeing round it (see `pathAhead`). */
  planned: { next: LaneSpot; turn: { curve: TurnCurve; end: number; into: ChunkLanes } | null } | undefined;
  /** The glide onto a new lane under way: the offsets it started from, and how far through it is. */
  glide: { x: number; z: number; yaw: number; t: number; seconds: number } | undefined;
  /** How this driver drives among the others: patience, and how readily it passes. */
  temperament: Temperament;
  /** What it last slowed or stopped for: for measuring. */
  cause: Lead['who'];
  /** Seconds held up by the player standing in its way, and until it may sound the horn again. */
  held: number;
  /**
   * The player on foot in its way: for how long, seconds, and whether they
   * were this frame. Until this driver has had `reaction` seconds to see
   * them, the driver has not, and drives on.
   */
  noticed: number;
  playerInWay: boolean;
  /** How long this driver takes to see someone step out in front and brake, seconds. */
  reaction: number;
  /** A police car: whether it is responding, blue lights and siren, and where in their beat its lamps are. */
  police?: { responding: boolean; phase: number; pursuing?: boolean };
  /** Seconds left of giving way to a responding police car behind it. */
  yielding: number;
  /** A responding police car: seconds left out in the oncoming lane, passing what will not pull over. */
  passing: number;
  /** A motorbike's lean into its turn, radians, positive to the left. */
  lean: number;
  /** Whether the lane it is on (`key`) has one the other way beside it, to pass in. */
  oncoming?: { key: string; yes: boolean };
  /**
   * How far over it is pulled, metres, positive to the right: over toward
   * the verge giving way, out toward the middle a police car passing them.
   * Eased toward `asideWant`; drawn, and solid, there (`asideX`, `asideZ`).
   */
  aside: number;
  asideWant: number;
  asideX: number;
  asideZ: number;
  /** Seconds left standing after running someone down: braking hard, then stopped. */
  struck: number;
  nagged: number;
  /** The wheels' roll and the front wheels' steer, radians. */
  wheelSpin: number;
  steer: number;
  /** Seconds left of moving over for a horn, or for the player close behind. */
  honked: number;
  /** How far round the ring it has gone, radians, and its heading a frame ago to measure that by. */
  roundTurned: number;
  lastDX: number;
  lastDZ: number;
  /** A bus on its line: its trip, how far along it, the stop it is coming to. See `core/transit/service.ts`. */
  bus?: BusService;
  /** Its indicator on, pulling out from a stop. */
  signalling?: boolean;
}

/**
 * Something moving through the traffic that it does not drive: a tram, a
 * section at a time. Cars follow one ahead of them, stop for one standing
 * in their way, and give way to one crossing it, whatever the timing: a
 * tram cannot stop or swerve for them.
 */
export interface Mover extends Footprint {
  y: number;
  speed: number;
}

export interface TrafficOptions {
  /** Most cars kept around the player. */
  population: number;
  /** Spawn no closer than this, once the first cars exist. */
  spawnMin: number;
  spawnMax: number;
  /** Retire any car further than this. */
  despawn: number;
}

export const DEFAULT_TRAFFIC_OPTIONS: TrafficOptions = {
  population: 72,
  spawnMin: 200,
  spawnMax: 420,
  despawn: 480,
};

const KINDS: VehicleSpecies[] = ['carHatch', 'carSedan', 'carEstate', 'carSuv', 'carVan', 'carPickup', 'truckBox', 'truckSemi',
  'busArea', 'busExpress', 'busMetro', 'busCity'];
const CAR_KIND: Record<VehicleSpecies, CarKind> = {
  carHatch: 'hatch', carSedan: 'sedan', carEstate: 'estate', carSuv: 'suv', carVan: 'van', carPickup: 'pickup', parkedMoto: 'hatch',
  truckBox: 'truck', truckSemi: 'semi', busArea: 'busArea', busExpress: 'busExpress', busMetro: 'busMetro', busCity: 'busCity',
  parkedScooter: 'hatch',
};
const isTruckSpecies = (s: VehicleSpecies) => s === 'truckBox' || s === 'truckSemi';
const isBusSpecies = (s: VehicleSpecies) => s === 'busArea' || s === 'busExpress' || s === 'busMetro' || s === 'busCity';
/** Heavy: driven as a truck is, gently, and kept to the right-hand lane. */
const isHeavy = (s: VehicleSpecies) => isTruckSpecies(s) || isBusSpecies(s);
/** Which bus wears which design. */
const BUS_SPECIES: Record<BusLook, VehicleSpecies> = { area: 'busArea', express: 'busExpress', metro: 'busMetro', plain: 'busCity' };
/** Over toward the kerb at a stop, metres, on a road with no lane to its right. */
const BUS_KERB = 0.7;
/** How far past a lane's end along its way a bus heads for in choosing where on, metres. */
const ROUTE_AHEAD = 30;
/** Most buses kept about the player, besides the cars. */
const MAX_BUSES = 14;
/** A bus is put on its road only this far from the player or further, once the first are out, metres. */
const BUS_SPAWN_MIN = 140;
/** A bus whose way is further than this from any lane its way does not take it to. */
const BUS_LANE_REACH = 6;
/**
 * Pulled over toward the verge for police car `by`, far enough that it may
 * go by: the two of them as far apart across the lane as their halves are
 * wide, the one in toward the verge, the other out toward the middle.
 */
function pulledOver(car: { yielding: number; aside: number; species: VehicleSpecies }, by: { aside: number; species: VehicleSpecies }): boolean {
  return car.yielding > 0 && car.aside - by.aside > VEHICLE_SIZE[car.species].halfWidth + VEHICLE_SIZE[by.species].halfWidth + 0.1;
}
/** What the police drive: saloons and estates, and in a city motorbikes too. */
const POLICE_KINDS: VehicleSpecies[] = ['carSedan', 'carEstate', 'parkedMoto'];
/** Where each one's light bar stands: on a car's roof, on a motorbike's tail behind the rider. */
const ROOF: Partial<Record<VehicleSpecies, { y: number; z: number }>> = {
  carSedan: roofTop('sedan'), carEstate: roofTop('estate'), parkedMoto: { y: 1.0, z: 0.72 },
};
/** How wide a light bar is, as a share of a car's: a motorbike's is a narrow pair of lamps. */
const BAR_WIDTH: Partial<Record<VehicleSpecies, number>> = { parkedMoto: 0.4 };
/** Built-up enough, by the traffic's measure of it, for police on motorbikes; and how many of the police are then. */
const BIKE_ROADS = 22;
const BIKE_SHARE = 0.3;
/** Trucks' top speeds, m/s: 85 km/h for a box truck, 80 with a trailer, Swedish law's limits for heavy trucks. */
const TRUCK_TOP: Partial<Record<VehicleSpecies, number>> = {
  truckBox: 85 / 3.6, truckSemi: 80 / 3.6, busArea: 80 / 3.6, busMetro: 80 / 3.6, busCity: 80 / 3.6, busExpress: 100 / 3.6,
};
/**
 * The share of the traffic that is trucks, by road style: most on the
 * motorways and trunk roads, where the long hauls are, articulated trucks
 * the most of them; a few box trucks delivering on a town's main roads; on a
 * residential street hardly any.
 */
const TRUCK_SHARE: Partial<Record<number, { box: number; semi: number }>> = {
  0: { box: 0.05, semi: 0.11 }, 1: { box: 0.05, semi: 0.05 }, 2: { box: 0.04, semi: 0.02 }, 3: { box: 0.02, semi: 0 },
};
/** Fleet colours for truck cabs: white, blues, reds, a green, a yellow, black. */
const CAB_PAINTS = [0xf0f0ec, 0xf0f0ec, 0x23498c, 0x2f6fb5, 0xb3261e, 0xc8432b, 0x2e5d3b, 0xe0b028, 0x1f2124];
/** The furthest a traffic truck's trailer swings round on its kingpin, radians. */
const TRAILER_SWING = 1.1;
/** The sharpest the front wheels turn, radians. */
const STEER_LOCK = 0.6;
/** How long a car whose tile is being rebuilt drives on blind before it is let go, seconds. */
const ADRIFT = 5;
/** How far before a lane's end a car chooses its way on, metres. */
const PLAN = 50;
/** How long a car waits to pull out before it goes anyway, seconds: no road stays blocked for ever. */
const PATIENCE = 20;
/** Seconds a car stands still before it pushes on through other cars, and for how long it does. */
const STUCK = 25;
const UNSTICK = 4;
/** Seconds between one change of lanes and the next. */
const SETTLE = 5;
/** No changing lanes this close to a lane's end, metres: the junction comes first. */
const NO_CHANGE = 60;
/**
 * How far round a roundabout a car goes before it may leave, radians: past
 * the first exit's quarter turn or so it takes any exit at even odds, and
 * three quarters of the way round it takes the next. Nobody goes round twice.
 */
const ROUND_MAYBE = 1.0;
const ROUND_MUST = 4.4;
/** How far ahead a car looks for what it must keep its distance from, metres. */
const LOOKAHEAD = 70;
/**
 * How long a driver takes to see someone step out in front and brake,
 * seconds: from the quickest to the slowest, as for something unexpected.
 * At 50 km/h that is 10 to 20 m driven before the brakes go on.
 */
const REACTION = [0.75, 1.5] as const;
/** How long a driver who has run someone down stands, seconds. */
const STRUCK_STAND = 8;
/** How fast a responding police car crosses a junction it has not the right of way at, m/s (15 km/h). */
const JUNCTION_CREEP = 15 / 3.6;
/** Chasing: how much faster than the road, as a share, and the most over, m/s. */
const PURSUING_SHARE = 1.6;
const PURSUING_OVER = 16;
/** How far into a pursued player's gap a chasing police car closes, metres: to contact. */
const CLOSE_IN = 2.5;
/** How near the one chased police cars join the chase, metres. */
const PURSUE_RANGE = 700;
/** How far a police car sees, metres. */
export const POLICE_SEES = 90;
/** How many of the nearest chasing police cars are asked whether they have a way off the road to the one they chase. */
const CHASER_TRIES = 6;
/** How near a stopped chasing police car catches the player, metres. */
const CATCHES = 9;
/** How long a police car pulled up for its officer to go on foot stands, seconds. */
const PULLED_UP = 40;
/** How far off its road the one chased may be, metres, for a police car level with them to pull up there. */
const PULL_UP_ACROSS = 35;

/** How far out a responding police car pulls to pass in the oncoming lane, metres; how long it stays out at least, seconds. */
const ONCOMING_ASIDE = 3.4;
const PASSING = 2.5;
/** How far ahead the oncoming lane must be clear to pull out into it, and to stay out, metres. */
const ONCOMING_CLEAR = 110;
const ONCOMING_ABORT = 45;
/** How near a junction a responding police car makes everyone else there wait for it, metres. */
const POLICE_HEARD = 45;
/** How far apart the points of a car's road ahead are taken, metres. */
const PATH_STEP = 2;
/**
 * An impatient driver's way with the pedals: quicker off the line and
 * closer behind than `CAR_DRIVER`.
 */
const IMPATIENT_DRIVER = { maxAccel: 2.1, comfortBrake: 2.6, minGap: 2, headway: 0.95 };

/** Something a car must stop short of, and what it is, as `Traffic.leader` finds it. */
interface Lead {
  gap: number;
  speed: number;
  /** The fastest it should be going now to pass someone close by its way slowly. */
  cap: number;
  /** What sets the gap: a person (braked for as hard as it takes), the player, or other traffic. */
  who: 'person' | 'player' | 'traffic';
}

/** A traffic vehicle taken out of the traffic: what it is, where, which way, how fast, and its trailer's heading. */
export interface Released {
  species: VehicleSpecies;
  paint: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  vx: number;
  vz: number;
  trailerYaw?: number;
  /** A police car, and whether it was responding. */
  police?: { responding: boolean };
}

/**
 * Two traffic vehicles run into each other, or one into a wreck in the road
 * (`obstacle`, its number in what `setObstacles` gave): the line of the hit
 * (unit, scene x, z, from `a` toward the other), where they met, and how
 * fast they were closing, m/s.
 */
export interface TrafficCrash {
  a: Released;
  b?: Released;
  obstacle?: number;
  nx: number;
  nz: number;
  x: number;
  z: number;
  closing: number;
}

/**
 * Whether two vehicles closing at `closing` m/s would crush either: a
 * crash, not a nudge they both drive on from. By weight and stiffness, as
 * the player's crashes are (`crushSpeeds`).
 */
function damaging(a: VehicleSpecies, b: VehicleSpecies, closing: number): boolean {
  const build = (s: VehicleSpecies) => (isHeavy(s) ? 'truck' : s === 'parkedMoto' ? 'moto' : 'car');
  const { first, second } = crushSpeeds(massOf(a), massOf(b), closing, STIFFNESS[build(a)], STIFFNESS[build(b)]);
  return Math.max(first, second) * 3.6 > KNOCK_KMH;
}

/** Closer than this to the player, metres, traffic can crash; further, nobody would see it. */
const CRASH_RANGE = 250;
/** Overlapping deeper than this, metres, and closing faster than this, m/s (14 km/h), is a crash. */
const CRASH_DEPTH = 0.2;
const CRASH_SPEED = 4;

/** Something the traffic can see people through: the villagers. */
export interface StreetPeople {
  readonly street: readonly StreetPerson[];
}

const projection: LaneProjection = { distance: 0, off: 0, dx: 0, dz: 1 };
/** A car's road ahead, x and z every `PATH_STEP` metres from its middle; filled by `pathAhead`. */
const path = new Float32Array((Math.floor(LOOKAHEAD / PATH_STEP) + 1) * 2);
/** The box round `path`: min x, min z, max x, max z. */
const pathBox = new Float32Array(4);
const reading: PersonReading = { stopAt: Infinity, passAt: Infinity };
const waiting: StreetPerson = { x: 0, y: 0, z: 0, vx: 0, vz: 0, aimX: NaN, aimZ: NaN };
const sample: LaneSample = { x: 0, y: 0, z: 0, dx: 0, dz: 1, grade: 0 };
const ahead: LaneSample = { x: 0, y: 0, z: 0, dx: 0, dz: 1, grade: 0 };
const leaving: LaneSample = { x: 0, y: 0, z: 0, dx: 0, dz: 1, grade: 0 };
const matrix = new Matrix4();
const position = new Vector3();
const rotation = new Quaternion();
const euler = new Euler(0, 0, 0, 'YXZ');
const unit = new Vector3(1, 1, 1);
const colour = new Color();
const wheelMatrix = new Matrix4();
const trailerMatrix = new Matrix4();
const barMatrix = new Matrix4();
const barScale = new Matrix4();
const wheelPosition = new Vector3();
const wheelRotation = new Quaternion();
const wheelEuler = new Euler(0, 0, 0, 'YXZ');
const wheelScale = new Vector3(1, 1, 1);

/** Whether a lane ends where it began: a ring drawn as one closed way. */
function closed(lane: Lane): boolean {
  const p = lane.points, n = p.length / 3;
  return lane.length > 20 && Math.hypot(p[0] - p[(n - 1) * 3], p[2] - p[(n - 1) * 3 + 2]) < 1;
}

function wrapAngle(a: number): number {
  return Math.atan2(Math.sin(a), Math.cos(a));
}

export class Traffic {
  readonly group = new Group();
  private readonly cars: Car[] = [];
  private readonly meshes = new Map<VehicleSpecies, InstancedMesh>();
  private readonly models = new Map<VehicleSpecies, TrafficModel>();
  private readonly trailers: InstancedMesh;
  private readonly wheelsLeft: InstancedMesh;
  private readonly wheelsRight: InstancedMesh;
  /** The radius the wheel meshes are built at; each car's are scaled to its own. */
  private readonly wheelRadius: number;
  private readonly rng = makeRng(0x7aff1c);
  private readonly opts: TrafficOptions;
  private populated = false;
  private wanted = 0;
  /** How many cars the roads about call for: how built-up the place is, for how many are police. */
  private roads = 0;
  /** The police cars' bodies, one mesh a kind, in the livery of the country (`setLivery`); their light bars. */
  private readonly policeMeshes = new Map<VehicleSpecies, InstancedMesh>();
  private livery: Livery = liveryOf(undefined);
  readonly policeLights: PoliceLights;
  /** Every car's headlights, tail and brake lights; where they go on each body, and on the trailer. */
  readonly carLights: CarLights;
  private readonly lamps = new Map<VehicleSpecies, LampLayout>();
  private readonly trailerLamps: LampLayout;
  /** A clock for the lamps' flashing, seconds. */
  private policeClock = 0;
  /** Where the one they are chasing is, scene x, z, while they are; and how many more police to send in. */
  private chase: { x: number; z: number } | undefined;
  private forcePolice = 0;
  private sinceSurvey = Infinity;
  private scale = 1;
  private serial = 0;
  /** The player's velocity, from how they moved between frames, m/s. */
  private playerVX = 0;
  private playerVZ = 0;
  private lastPX = NaN;
  private lastPZ = NaN;
  private playerY = NaN;
  /** Crashes found since last taken, and how many there have been. */
  private readonly crashed: TrafficCrash[] = [];
  crashCount = 0;
  /** Vehicles standing in the road: wrecks and cars left there. See `setObstacles`. */
  private obstacles: Array<Footprint & { species?: VehicleSpecies }> = [];
  /** The people in the street, to stop for as for the player. */
  private people: StreetPeople | undefined;
  /** Horn toots since last asked, scene x and z each: for the sound. */
  private readonly toots: number[] = [];
  /** How many times drivers have sounded the horn, all told. */
  hornsSounded = 0;
  /** The player's truck's trailer, if they are pulling one: something to stop for, as they are. */
  private playerTrailer: { x: number; z: number; dx: number; dz: number; halfLength: number; halfWidth: number } | undefined;
  /** The trams about, each section as a box with its speed: see `setMovers`. */
  private movers: Mover[] = [];
  /** Public transport about: each trip, laid out in the scene. See `setTransit`. */
  private transit: Array<{ trip: TransitTrip; layout: TripLayout }> = [];
  private busesOut = false;

  constructor(private readonly network: LaneNetwork, options: Partial<TrafficOptions> = {}) {
    this.opts = { ...DEFAULT_TRAFFIC_OPTIONS, ...options };
    const capacity = Math.max(1, this.opts.population);
    for (const kind of KINDS) {
      const model = trafficModel(CAR_KIND[kind]);
      this.models.set(kind, model);
      const mesh = movingInstances(model.body, capacity, `traffic:${kind}`);
      this.meshes.set(kind, mesh);
      this.group.add(mesh);
    }
    const semi = this.models.get('truckSemi')!.trailer!;
    this.trailers = movingInstances(semi.body, capacity, 'traffic:trailers');
    this.group.add(this.trailers);
    const wheels = trafficWheels();
    this.wheelRadius = wheels.radius;
    // Two a side for a car or a truck, three more a side under a trailer.
    this.wheelsLeft = movingInstances(wheels.left, capacity * 5, 'traffic:wheels-left');
    this.wheelsRight = movingInstances(wheels.right, capacity * 5, 'traffic:wheels-right');
    this.group.add(this.wheelsLeft, this.wheelsRight);
    const look = liveried();
    this.setLivery(this.livery);
    this.models.set('parkedMoto', trafficMotorbike());
    for (const kind of POLICE_KINDS) {
      const mesh = movingInstances(this.models.get(kind)!.body, capacity, `traffic:police-${kind}`, look);
      this.policeMeshes.set(kind, mesh);
      this.group.add(mesh);
    }
    this.policeLights = new PoliceLights(capacity);
    this.policeLights.addTo(this.group);
    for (const [kind, model] of this.models) this.lamps.set(kind, lampLayout(model.body, kind === 'parkedMoto', model.lamps));
    this.trailerLamps = lampLayout(semi.body, false, semi.lamps);
    this.carLights = new CarLights(capacity);
    this.carLights.addTo(this.group);
  }

  /** Dress the police in `livery`: the country's, where the player is. */
  setLivery(livery: Livery): void {
    this.livery = livery;
    const u = LIVERY;
    u.uPattern.value = ['battenburg', 'band', 'doors', 'stripe'].indexOf(livery.pattern);
    u.uBase.value.setHex(livery.base);
    u.uA.value.setHex(livery.a);
    u.uB.value.setHex(livery.b);
  }

  /**
   * The police motorbikes, for their riders to be posed on them: each one's
   * id, where it is drawn (world matrix), how fast it goes and how far it
   * leans. Undefined motorbikes are not police; there are no others.
   */
  bikes(): Array<{ id: number; matrix: Matrix4; speed: number; lean: number }> {
    const out: Array<{ id: number; matrix: Matrix4; speed: number; lean: number }> = [];
    const mesh = this.policeMeshes.get('parkedMoto')!;
    let k = 0;
    for (const car of this.cars) {
      if (car.species !== 'parkedMoto' || car.police === undefined) continue;
      const m = new Matrix4();
      mesh.getMatrixAt(k++, m);
      out.push({ id: car.id, matrix: m, speed: car.speed, lean: car.lean });
    }
    return out;
  }

  /**
   * Chase whoever is at scene `target` with `wanted` police cars, or stop
   * chasing with none. Every police car within `PURSUE_RANGE` joins in,
   * lights and siren on, and more are sent in from out of sight until there
   * are as many as wanted. Stopping, they all stand down.
   */
  pursue(target: { x: number; z: number } | undefined, wanted: number): void {
    this.chase = target;
    if (target === undefined) {
      for (const car of this.cars) {
        if (car.police?.pursuing !== true) continue;
        car.police.pursuing = false;
        car.police.responding = false;
      }
      this.forcePolice = 0;
      return;
    }
    let n = 0;
    for (const car of this.cars) {
      if (car.police === undefined) continue;
      if (car.police.pursuing !== true && Math.hypot(car.x - target.x, car.z - target.z) > PURSUE_RANGE) continue;
      car.police.pursuing = true;
      car.police.responding = true;
      n++;
    }
    this.forcePolice = Math.max(0, wanted - n);
  }

  /** The police cars within `within` (by default as far as one sees) of scene (x, z), on its level: where each is, and how far. Whether one sees past what stands between is the game's to say. */
  policeNear(x: number, z: number, y: number, within = POLICE_SEES): Array<{ x: number; y: number; z: number; d: number }> {
    const out: Array<{ x: number; y: number; z: number; d: number }> = [];
    for (const car of this.cars) {
      if (car.police === undefined || Math.abs(car.y - y) > 6) continue;
      const cx = car.x + car.offsetX + car.asideX, cz = car.z + car.offsetZ + car.asideZ;
      const d = Math.hypot(cx - x, cz - z);
      if (d < within) out.push({ x: cx, y: car.y, z: cz, d });
    }
    return out;
  }

  /** Whether a chasing police car has stopped beside scene (x, z): the one it chases, caught. */
  caught(x: number, z: number): boolean {
    for (const car of this.cars) {
      if (car.police?.pursuing !== true || car.speed > 3) continue;
      if (Math.hypot(car.x + car.offsetX + car.asideX - x, car.z + car.offsetZ + car.asideZ - z) < CATCHES) return true;
    }
    return false;
  }

  /** The nearest chasing police car stopped within `within` of scene (x, z): where it stands, for an officer to get out. */
  stoppedNear(x: number, z: number, within: number): { id: number; x: number; y: number; z: number; dx: number; dz: number } | undefined {
    let best: Car | undefined, bestD = within;
    for (const car of this.cars) {
      if (car.police?.pursuing !== true || car.speed > 1) continue;
      const d = Math.hypot(car.x - x, car.z - z);
      if (d < bestD) { bestD = d; best = car; }
    }
    return best === undefined ? undefined : { id: best.id, x: best.x + best.offsetX + best.asideX, y: best.y, z: best.z + best.offsetZ + best.asideZ, dx: best.dx, dz: best.dz };
  }

  /**
   * The car standing still with scene (x, y, z) within `reach` metres of its
   * body: one waiting at a light or in a queue, for the player to pull its
   * driver out of. Not a police car, nor a motorbike.
   */
  standingAt(x: number, y: number, z: number, reach: number): { id: number; species: VehicleSpecies; x: number; y: number; z: number; yaw: number } | undefined {
    for (const car of this.cars) {
      if (car.speed > 0.5 || car.police !== undefined || car.species === 'parkedMoto') continue;
      const size = VEHICLE_SIZE[car.species];
      const cx = car.x + car.offsetX + car.asideX, cz = car.z + car.offsetZ + car.asideZ;
      const dx = -Math.sin(car.yaw), dz = -Math.cos(car.yaw);
      const along = (x - cx) * dx + (z - cz) * dz, across = (x - cx) * dz - (z - cz) * dx;
      if (Math.abs(along) > size.halfLength + reach || Math.abs(across) > size.halfWidth + reach) continue;
      if (y < car.y - 0.5 || y > car.y + size.height + reach) continue;
      return { id: car.id, species: car.species, x: cx, y: car.y, z: cz, yaw: car.yaw };
    }
    return undefined;
  }

  /**
   * Take the nearest chasing police car (not a motorbike) within `within`
   * of scene (x, z) out of the traffic, to leave the road after the one it
   * chases: as it is, for the game to drive. Only one `clear` says has an
   * open way from where it is (x, z, y) to them, of the nearest
   * `CHASER_TRIES`, nearest first.
   */
  takeChaser(x: number, z: number, within: number, clear: (x: number, z: number, y: number, id: number) => boolean = () => true): Released | undefined {
    const near = this.cars
      .filter(car => car.police?.pursuing === true && car.species !== 'parkedMoto' && car.adrift <= 0 && Math.hypot(car.x - x, car.z - z) < within)
      .sort((a, b) => Math.hypot(a.x - x, a.z - z) - Math.hypot(b.x - x, b.z - z));
    for (const car of near.slice(0, CHASER_TRIES)) {
      if (clear(car.x, car.z, car.y, car.id)) return this.release(car.id);
    }
    return undefined;
  }

  /**
   * Pull up the nearest chasing police car within `within` of scene (x, z),
   * whatever its way to them: it stands, for its officer to go on foot.
   * Where it stands; undefined with none near.
   */
  pullUp(x: number, z: number, within: number): { id: number; x: number; y: number; z: number; dx: number; dz: number } | undefined {
    let best: Car | undefined, bestD = within;
    for (const car of this.cars) {
      if (car.police?.pursuing !== true) continue;
      const d = Math.hypot(car.x - x, car.z - z);
      if (d < bestD) { bestD = d; best = car; }
    }
    if (best === undefined) return undefined;
    best.struck = PULLED_UP;
    return { id: best.id, x: best.x + best.offsetX + best.asideX, y: best.y, z: best.z + best.offsetZ + best.asideZ, dx: best.dx, dz: best.dz };
  }

  /** How many police cars are chasing. */
  get pursuing(): number {
    return this.cars.reduce((n, c) => n + (c.police?.pursuing === true ? 1 : 0), 0);
  }

  /** How built-up the place is: how many cars its roads call for (a city's streets, 60 or so). */
  get urban(): number {
    return this.roads;
  }

  /** How many police cars are about, and how many of them responding. */
  get police(): { cars: number; responding: number } {
    let cars = 0, responding = 0;
    for (const car of this.cars) {
      if (car.police === undefined) continue;
      cars++;
      if (car.police.responding) responding++;
    }
    return { cars, responding };
  }

  /**
   * Set the police cars within `radius` of scene (x, z) responding, blue
   * lights and siren, or standing down. Returns how many.
   */
  respond(x: number, z: number, radius: number, on: boolean): number {
    let n = 0;
    for (const car of this.cars) {
      if (car.police === undefined || Math.hypot(car.x - x, car.z - z) > radius) continue;
      car.police.responding = on;
      n++;
    }
    return n;
  }

  /**
   * The responding police cars as a listener at scene (x, z) going (vx, vz)
   * hears them: how far, and how fast each is closing.
   */
  sirens(x: number, z: number, vx = this.playerVX, vz = this.playerVZ): SirenSource[] {
    const out: SirenSource[] = [];
    for (const car of this.cars) {
      if (car.police?.responding !== true) continue;
      const rx = x - car.x, rz = z - car.z, d = Math.hypot(rx, rz) || 1;
      const closing = ((car.dx * car.speed - vx) * rx + (car.dz * car.speed - vz) * rz) / d;
      out.push({ id: car.id, kind: this.livery.siren, distance: d, closing });
    }
    return out;
  }

  /**
   * Who gives way to a responding police car: every car it is coming up
   * behind on its road (`givesWay`), for a second after, renewed while it
   * still is. Once a frame, before anyone drives.
   */
  private giveWay(dt: number): void {
    for (const car of this.cars) {
      car.yielding = Math.max(0, car.yielding - dt);
      car.asideWant = car.yielding > 0 ? PULL_OVER : 0;
    }
    // The player, blue lights on, among them: where they are and which way they go.
    const speed = Math.hypot(this.playerVX, this.playerVZ);
    const player = this.playerSiren && speed > 1
      ? { x: this.lastPX, z: this.lastPZ, dx: this.playerVX / speed, dz: this.playerVZ / speed, y: this.playerY } : undefined;
    const responding = this.cars.filter(c => c.police?.responding === true) as Array<{ x: number; z: number; dx: number; dz: number; y: number }>;
    if (player !== undefined) responding.push(player);
    for (const p of responding) {
      for (const car of this.cars) {
        if (car === p || car.police?.responding === true || !(Math.abs(car.y - p.y) <= 3 || Number.isNaN(p.y))) continue;
        if (!givesWay(car.x, car.z, car.dx, car.dz, p.x, p.z, p.dx, p.dz)) continue;
        car.yielding = 1;
        car.asideWant = PULL_OVER;
        // Close ahead in its own lane: out toward the middle to get by.
        const rx = car.x - p.x, rz = car.z - p.z;
        if ('asideWant' in p && rx * p.dx + rz * p.dz < 30 && Math.abs(rx * p.dz - rz * p.dx) < 2.2) (p as Car).asideWant = -PULL_OVER;
      }
    }
  }

  get count(): number {
    return this.cars.length;
  }

  /**
   * The cars within `radius` of scene (x, z) at height y, give or take a
   * bridge's clearance: what the autopilot drives among.
   */
  nearby(x: number, z: number, y: number, radius: number): Array<{ x: number; z: number; dx: number; dz: number; speed: number; halfLength: number }> {
    const out = [];
    for (const car of this.cars) {
      if (Math.hypot(car.x - x, car.z - z) > radius || Math.abs(car.y - y) > 4) continue;
      out.push({ x: car.x, z: car.z, dx: car.dx, dz: car.dz, speed: car.speed, halfLength: VEHICLE_SIZE[car.species].halfLength });
      const t = this.trailerBody(car);
      if (t !== undefined) out.push({ x: t.x, z: t.z, dx: t.dx, dz: t.dz, speed: car.speed, halfLength: t.halfLength });
    }
    return out;
  }

  /**
   * Every car within `radius` of scene (x, z), as a box with its speed:
   * what the people in the street look out for.
   */
  around(x: number, z: number, radius: number): Array<{ x: number; y: number; z: number; dx: number; dz: number; speed: number; halfLength: number; halfWidth: number }> {
    const out = [];
    for (const car of this.cars) {
      if (Math.abs(car.x - x) > radius || Math.abs(car.z - z) > radius) continue;
      const size = VEHICLE_SIZE[car.species];
      out.push({ x: car.x + car.offsetX + car.asideX, y: car.y, z: car.z + car.offsetZ + car.asideZ, dx: -Math.sin(car.yaw), dz: -Math.cos(car.yaw), speed: car.speed, halfLength: size.halfLength, halfWidth: size.halfWidth });
      const t = this.trailerBody(car);
      if (t !== undefined) out.push({ x: t.x, y: car.y, z: t.z, dx: t.dx, dz: t.dz, speed: car.speed, halfLength: t.halfLength, halfWidth: t.halfWidth });
    }
    return out;
  }

  /**
   * The vehicles within `radius` of scene (x, z), as the sound hears them:
   * which, what kind, where, and how fast which way. See `three/audio/voices.ts`.
   */
  heard(x: number, z: number, radius: number): Array<{ id: number; species: VehicleSpecies; x: number; y: number; z: number; vx: number; vz: number; speed: number }> {
    const out = [];
    for (const car of this.cars) {
      if (Math.abs(car.x - x) > radius || Math.abs(car.z - z) > radius) continue;
      out.push({
        id: car.id, species: car.species, x: car.x + car.offsetX + car.asideX, y: car.y + 0.8, z: car.z + car.offsetZ + car.asideZ,
        vx: car.dx * car.speed, vz: car.dz * car.speed, speed: car.speed,
      });
    }
    return out;
  }

  /** Who is in the street besides the player: the villagers. */
  setPeople(people: StreetPeople): void {
    this.people = people;
  }

  /** Where drivers have sounded the horn since the last call, scene x, z pairs; for the sound. */
  takeToots(): number[] {
    return this.toots.splice(0, this.toots.length);
  }

  /** Whether the player is on foot this frame: someone a driver must see step out, rather than another car. */
  playerOnFoot = false;
  /** Whether the player is driving with blue lights and a siren: given way to as a responding police car is. */
  playerSiren = false;

  /**
   * A car striking someone on foot at scene (x, z), feet at `y`, going at
   * (vx, vz) m/s (by default the player, as the traffic sees them move):
   * closing on them faster than `KNOCKED_DOWN`, the hit, shared by weight,
   * and the species that struck. The driver stands on the brakes and stops.
   * Undefined when no car does.
   */
  strikePerson(x: number, y: number, z: number, vx = this.playerVX, vz = this.playerVZ): { hit: Hit; species: VehicleSpecies } | undefined {
    for (const car of this.cars) {
      if (Math.abs(car.y - y) > 1.5 || Math.abs(car.x - x) > 12 || Math.abs(car.z - z) > 12) continue;
      const size = VEHICLE_SIZE[car.species];
      const dx = -Math.sin(car.yaw), dz = -Math.cos(car.yaw);
      const hit = strike({
        x: car.x + car.offsetX + car.asideX, z: car.z + car.offsetZ + car.asideZ, dx, dz, halfLength: size.halfLength, halfWidth: size.halfWidth,
        vx: dx * car.speed, vz: dz * car.speed, mass: massOf(car.species),
      }, x, z, vx, vz);
      if (hit === undefined || hit.closing < KNOCKED_DOWN) continue;
      car.speed = Math.max(0, car.speed + hit.vehicle.x * dx + hit.vehicle.z * dz);
      car.struck = STRUCK_STAND;
      return { hit, species: car.species };
    }
    return undefined;
  }

  /** Where the trailer the player is pulling is, as a box, this frame; undefined when there is none. */
  setPlayerTrailer(box: { x: number; z: number; dx: number; dz: number; halfLength: number; halfWidth: number } | undefined): void {
    this.playerTrailer = box;
  }

  /** How busy the roads are, relative to an ordinary daytime hour. */
  setDensity(scale: number): void {
    this.scale = Math.max(0, scale);
  }

  /**
   * Drive every car on by `dt`. `(px, pz)` is the player, in scene metres,
   * and `py` their height: one flying over, or on a bridge above, is not in
   * any car's way.
   */
  update(dt: number, px: number, pz: number, py = NaN): void {
    this.playerY = py;
    if (this.opts.population <= 0) return;
    dt = Math.min(dt, 0.1);
    if (Number.isFinite(this.lastPX) && dt > 0) {
      // A teleport or a rebase is not a speed.
      const vx = (px - this.lastPX) / dt, vz = (pz - this.lastPZ) / dt;
      const ok = Math.hypot(vx, vz) < 80;
      const k = Math.min(1, dt * 4);
      this.playerVX += ((ok ? vx : 0) - this.playerVX) * k;
      this.playerVZ += ((ok ? vz : 0) - this.playerVZ) * k;
    }
    this.lastPX = px;
    this.lastPZ = pz;
    this.sinceSurvey += dt;
    if (this.sinceSurvey > 2) {
      this.sinceSurvey = 0;
      const roads = this.network.carsNear(px, pz, this.opts.spawnMax);
      this.roads = roads;
      this.wanted = Math.min(this.opts.population, Math.round(roads * this.scale));
    }
    this.spawn(px, pz);
    this.spawnBuses(px, pz);
    this.thin(px, pz);

    // Where everyone is, before anyone moves: each car keeps its distance
    // from the others as they were this frame.
    for (let i = this.cars.length - 1; i >= 0; i--) {
      const car = this.cars[i];
      const placed = car.adrift > 0 ? this.regain(car, dt) : this.locate(car) || this.regain(car, dt);
      if (!placed || Math.hypot(car.x - px, car.z - pz) > this.opts.despawn) this.cars.splice(i, 1);
    }

    this.giveWay(dt);
    for (let i = this.cars.length - 1; i >= 0; i--) {
      const car = this.cars[i];
      if (car.adrift > 0) continue;
      if (!this.drive(car, dt, px, pz)) this.cars.splice(i, 1);
    }
    this.draw(dt);
    this.crashes(px, pz);
  }

  /** Whether the player is at a car's own level, so on its road rather than over or under it. Unknown counts as yes. */
  private level(car: Car): boolean {
    return !(Math.abs(this.playerY - car.y) > 4);
  }

  /** Sample a car's lane for its logical position. False when its road has gone. */
  private locate(car: Car): boolean {
    const chunk = this.network.get(car.spot.chunk);
    const lane = chunk?.lanes[car.spot.lane];
    if (chunk === undefined || lane === undefined) return false;
    if (car.turn !== undefined) sampleTurn(car.turn.curve, car.turn.s, sample);
    else sampleLane(lane, car.spot.distance, sample);
    car.x = sample.x + chunk.group.position.x;
    car.y = sample.y + chunk.group.position.y;
    car.z = sample.z + chunk.group.position.z;
    car.dx = sample.dx;
    car.dz = sample.dz;
    car.grade = sample.grade;
    car.ring = lane.roundabout;
    return true;
  }

  /**
   * A car whose lane went with its tile: rebuilt tiles come back with the
   * same roads, so it drives straight on and takes up the lane under it as
   * soon as there is one. False once it has gone too long without.
   */
  private regain(car: Car, dt: number): boolean {
    car.adrift += dt;
    if (car.adrift > ADRIFT) return false;
    car.x += car.dx * car.speed * dt;
    car.z += car.dz * car.speed * dt;
    const spot = this.network.next(car.x, car.z, car.dx, car.dz, undefined, this.rng);
    if (spot === undefined) return true;
    const fromX = car.x + car.offsetX, fromZ = car.z + car.offsetZ, fromYaw = car.yaw;
    car.spot = spot;
    car.next = undefined;
    car.turn = undefined;
    if (!this.locate(car)) return true;
    car.adrift = 0;
    this.ease(car, fromX, fromZ, fromYaw);
    return true;
  }

  /** Draw a car where it was, easing onto where it now is over a second or two. */
  /**
   * Draw a car where it was and glide it onto where it now is over `seconds`,
   * along a smoothstep: no sideways speed at the start or the end, as a car
   * drifts across a lane, rather than darting off and settling.
   */
  private ease(car: Car, fromX: number, fromZ: number, fromYaw: number, seconds = 0.8): void {
    car.offsetX = fromX - car.x;
    car.offsetZ = fromZ - car.z;
    car.offsetYaw = wrapAngle(fromYaw - Math.atan2(-car.dx, -car.dz));
    const across = Math.hypot(car.offsetX, car.offsetZ);
    if (across > 8) car.offsetX = car.offsetZ = 0;
    // Never faster across than about 1.5 m/s at its quickest.
    car.glide = { x: car.offsetX, z: car.offsetZ, yaw: car.offsetYaw, t: 0, seconds: Math.max(seconds, across) };
  }

  /** The gap to, and speed of, whatever is ahead in this car's lane, and how fast it may pass anyone close by. */
  private leader(car: Car, px: number, pz: number): Lead {
    let gap = Infinity, speed = 0, cap = Infinity;
    let who: Lead['who'] = 'traffic';
    const own = VEHICLE_SIZE[car.species].halfLength;
    const consider = (ox: number, oz: number, halfLength: number, odx: number, odz: number, ospeed: number, any: boolean) => {
      const rx = ox - car.x, rz = oz - car.z;
      const along = rx * car.dx + rz * car.dz;
      if (along <= 0 || along > LOOKAHEAD) return false;
      const across = Math.abs(rx * car.dz - rz * car.dx);
      if (across > 2.2) return false;
      const facing = odx * car.dx + odz * car.dz;
      if (!any && facing < 0.4) return false;
      const g = along - own - halfLength;
      if (g < gap) { gap = g; speed = Math.max(0, ospeed * facing); return true; }
      return false;
    };
    // The player could be on foot or at the wheel, and either way is something to stop for.
    const g0 = gap, s0 = speed, w0 = who;
    const near = this.level(car);
    const playerSpeed = Math.hypot(this.playerVX, this.playerVZ);
    const pdx = playerSpeed > 0.5 ? this.playerVX / playerSpeed : car.dx;
    const pdz = playerSpeed > 0.5 ? this.playerVZ / playerSpeed : car.dz;
    if (near && consider(px, pz, 2.3, pdx, pdz, playerSpeed, true)) who = 'player';
    // A tram ahead on its tracks in the street: followed, as a car is.
    for (const m of this.movers) {
      if (Math.abs(m.y - car.y) < 3 && consider(m.x, m.z, m.halfLength, m.dx, m.dz, m.speed, false)) who = 'traffic';
    }
    const pt = this.playerTrailer;
    if (near && pt !== undefined && consider(pt.x, pt.z, pt.halfLength, pt.dx, pt.dz, playerSpeed, true)) who = 'player';
    // Round a bend the player is not straight ahead, but still in the way:
    // look along the road it will drive.
    // Its road ahead, worked out only when someone is near enough to be on it.
    const reach = LOOKAHEAD + 8;
    let someone = near && Math.abs(px - car.x) < reach && Math.abs(pz - car.z) < reach;
    for (const person of this.people?.street ?? []) {
      if (someone) break;
      someone = Math.abs(person.x - car.x) < reach && Math.abs(person.z - car.z) < reach && Math.abs(person.y - car.y) < 3;
    }
    let pathCount = -1;
    const getPath = (): number => (pathCount >= 0 ? pathCount : (pathCount = this.pathAhead(car)));
    const count = someone ? getPath() : 0;
    const onPath = near && count > 0 ? this.pathTo(car, count, px, pz) : Infinity;
    if (onPath - own - 2.3 < gap) {
      gap = onPath - own - 2.3;
      speed = Math.max(0, (pdx * car.dx + pdz * car.dz) * playerSpeed);
      who = 'player';
    }
    // Someone on foot stepping out in front is not seen at once: the driver
    // drives on until they have had time to see them. Standing in the road
    // well ahead, they are seen long before it matters; stepped out a few
    // car lengths in front, too late.
    car.playerInWay = who === 'player' && this.playerOnFoot;
    if (car.playerInWay && car.noticed < car.reaction && car.police?.pursuing !== true) { gap = g0; speed = s0; who = w0; }
    // Chasing the player, it does not hang back behind them: it closes right up.
    if (car.police?.pursuing === true && who === 'player') gap -= CLOSE_IN;
    // People in the street: stopped for if in the way or stepping into it,
    // passed slowly if close beside it. Someone waiting at a crossing is
    // stopped for as if already on it, by a driver who can stop in time.
    const halfWidth = VEHICLE_SIZE[car.species].halfWidth;
    for (const person of someone ? this.people?.street ?? [] : []) {
      if (Math.abs(person.y - car.y) > 3) continue;
      // Beside it now, or just behind: not away from them till it is past.
      if (bodyClearance(car.x + car.offsetX + car.asideX, car.z + car.offsetZ + car.asideZ, -Math.sin(car.yaw), -Math.cos(car.yaw), own, halfWidth, person.x, person.z) < CLOSE_BY) {
        cap = Math.min(cap, PASSING_SPEED);
      }
      // Nowhere near its road, now or in a moment, nor waiting to step onto it.
      const margin = halfWidth + 2 + Math.abs(person.vx) + Math.abs(person.vz) + (Number.isNaN(person.aimX) ? 0 : 3);
      if (person.x < pathBox[0] - margin || person.x > pathBox[2] + margin ||
        person.z < pathBox[1] - margin || person.z > pathBox[3] + margin) continue;
      readPerson(path, count, PATH_STEP, halfWidth, person, reading);
      if (reading.stopAt - own - 1 < gap) {
        gap = reading.stopAt - own - 1;
        speed = 0;
        who = 'person';
      }
      // Down to walking pace before its front comes within reach of them.
      if (reading.passAt < Infinity) cap = Math.min(cap, approachSpeed(reading.passAt - own - CLOSE_BY, PASSING_SPEED));
      if (Number.isNaN(person.aimX)) continue;
      waiting.x = person.aimX;
      waiting.z = person.aimZ;
      readPerson(path, count, PATH_STEP, halfWidth, waiting, reading);
      const stop = reading.stopAt - own - 2;
      if (stop < gap && canYield(car.speed, stop)) {
        gap = stop;
        speed = 0;
        who = 'person';
      }
    }
    // Anything standing or crawling on the road it will drive, round its turn
    // too, and any trailer there, moving or not: stopped short of. A car
    // following straight on sees the one ahead above; this is for what lies
    // across its way: a car with its nose over a line, a trailer swinging
    // through a junction, a wreck. A car waiting to merge into its lane is
    // let in, as above, not waited for. A car pushing on through, its last
    // resort, still stops for a trailer: that moves on when its truck does.
    const pushing = car.stuck > STUCK;
    {
      const own = VEHICLE_SIZE[car.species];
      // What lies across its way is waited for as standing.
      const blocking = (f: Footprint) => {
        if (Math.abs(f.x - car.x) > 45 || Math.abs(f.z - car.z) > 45) return;
        const pc = getPath();
        const at = firstOnPath(path, Math.min(pc, 20), PATH_STEP, own.halfWidth, f, 0.25, own.halfLength);
        const g = at - own.halfLength - 0.8;
        if (at < Infinity && g < gap) { gap = g; who = 'traffic'; speed = 0; }
      };
      for (const other of this.cars) {
        if (other === car || Math.abs(other.y - car.y) > 3) continue;
        if (Math.abs(other.x - car.x) > 58 || Math.abs(other.z - car.z) > 58) continue;
        const merging = other.next !== undefined && other.next !== null &&
          other.next.chunk === car.spot.chunk && other.next.lane === car.spot.lane && other.waited > 0;
        const out = car.aside < -ONCOMING_ASIDE * 0.7 && other.dx * car.dx + other.dz * car.dz > 0.7 &&
          Math.abs((other.x - car.x) * car.dz - (other.z - car.z) * car.dx) < 2;
        const givingWay = car.police?.responding === true && (pulledOver(other, car) || out);
        if (!pushing && other.speed < 2 && !merging && !givingWay) blocking(this.footprint(other));
        const t = this.trailerBody(other);
        if (t !== undefined) blocking(t);
      }
      // A wreck is edged past at the last, as a queue is: on one lane each
      // way there is no going round it.
      if (!pushing) for (const o of this.obstacles) blocking(o);
      // A tram standing or crawling across its way, stopped short of, pushing or not.
      for (const m of this.movers) if (m.speed < 2 && Math.abs(m.y - car.y) < 3) blocking(m);
      // Anyone whose way will cut across its own in the next few seconds:
      // the one that would get there later gives way (see `yields`). Roads
      // often cross with no junction where either way ends, and an on-ramp
      // joins a lane cars are already doing 100 km/h in; the give-way lines
      // above never see those. Not a car behind in its own lane, nor the one
      // ahead it follows; on a roundabout's ring, not one still coming in;
      // behind it, only one closing on it fast.
      for (const other of pushing ? [] : this.cars) {
        if (other === car || other.speed < 1 || Math.abs(other.y - car.y) > 3) continue;
        const rx = other.x - car.x, rz = other.z - car.z;
        if (Math.abs(rx) > 50 || Math.abs(rz) > 50) continue;
        const along = rx * car.dx + rz * car.dz, across = rx * car.dz - rz * car.dx;
        if (Math.abs(across) < 2 && other.dx * car.dx + other.dz * car.dz > 0.8) continue;
        if (car.ring && !other.ring) continue;
        const size = VEHICLE_SIZE[other.species];
        const meet = conflict(path, Math.min(getPath(), 25), PATH_STEP, own.halfLength,
          other.x, other.z, other.dx, other.dz, other.speed, 3, own.halfWidth + size.halfWidth + 0.5);
        if (meet === undefined) continue;
        const mine = Math.max(0, meet.mine - own.halfLength) / Math.max(car.speed, 2);
        const theirs = Math.max(0, meet.theirs - size.halfLength) / other.speed;
        // On its right: right of travel (dx, dz) is (-dz, dx).
        const onRight = -rx * car.dz + rz * car.dx > 0;
        // Behind it, only one coming up faster on a way that joins its own:
        // merging, it gives way to what is already in the lane.
        if (along < -own.halfLength && other.speed <= car.speed + 1) continue;
        // A bus is let through: whoever's way it crosses gives way to it.
        if (other.bus === undefined || car.bus !== undefined) {
          if (!yields(mine, theirs, onRight)) continue;
        }
        const g = meet.mine - own.halfLength - 1.5;
        if (g < gap) { gap = Math.max(0.1, g); speed = 0; who = 'traffic'; }
      }
    }
    // A tram about to cross its way: given way to, always.
    {
      const own = VEHICLE_SIZE[car.species];
      for (const m of this.movers) {
        if (m.speed < 1 || Math.abs(m.y - car.y) > 3 || Math.abs(m.x - car.x) > 60 || Math.abs(m.z - car.z) > 60) continue;
        // Not one it is following along the same way.
        if (m.dx * car.dx + m.dz * car.dz > 0.8 && Math.abs((m.x - car.x) * car.dz - (m.z - car.z) * car.dx) < 2) continue;
        const meet = conflict(path, Math.min(getPath(), 25), PATH_STEP, own.halfLength,
          m.x, m.z, m.dx, m.dz, m.speed, 4, own.halfWidth + m.halfWidth + 0.5);
        if (meet === undefined) continue;
        const g = meet.mine - own.halfLength - 2;
        if (g < gap) { gap = Math.max(0.1, g); speed = 0; who = 'traffic'; }
      }
    }
    // Stuck too long, it noses on through whatever has it boxed in.
    if (car.stuck > STUCK && who === 'traffic') return { gap: Math.max(0.1, gap), speed, cap, who };
    for (const other of this.cars) {
      if (other === car || Math.abs(other.y - car.y) > 3) continue;
      const rx = car.x - other.x, rz = car.z - other.z;
      if (Math.abs(rx) > LOOKAHEAD + 18 || Math.abs(rz) > LOOKAHEAD + 18) continue;
      // A car standing at the end of its lane, waiting to merge into this
      // car's, is giving way to it and not in its way: where an on-ramp runs
      // in beside a carriageway the two lanes are closer than a lane's width,
      // and stopping behind it, while it waits for this car to pass, locks
      // the merge.
      const merging = other.next !== undefined && other.next !== null &&
        other.next.chunk === car.spot.chunk && other.next.lane === car.spot.lane;
      if (merging && other.waited > 0 && other.speed < 1) continue;
      // Where the other car has this one ahead of it as they converge.
      const back = rx * other.dx + rz * other.dz;
      const converging = back > 0 && Math.abs(rx * other.dz - rz * other.dx) <= 2.2 && other.dx * car.dx + other.dz * car.dz < 0.95;
      if (converging) {
        // Going round a roundabout, it does not wait for a car still coming
        // in; one coming in always waits for the ring.
        if (car.ring && !other.ring) continue;
        // Otherwise each could have the other just ahead and wait for ever.
        // The one with the other further ahead of it gives way; the other goes.
        const along = -(rx * car.dx + rz * car.dz);
        if (!(other.ring && !car.ring) && (along < back || (along === back && car.id < other.id))) continue;
      }
      if (car.stuck > STUCK) continue;
      // Responding, it goes by those pulled over for it rather than wait
      // behind them; one still to move over a lane, it waits behind. Out in
      // the oncoming lane, it goes by everyone in the lane it has left.
      if (car.police?.responding === true && pulledOver(other, car)) continue;
      if (car.aside < -ONCOMING_ASIDE * 0.7 && Math.abs((other.x - car.x) * car.dz - (other.z - car.z) * car.dx) < 2) continue;
      if (consider(other.x, other.z, VEHICLE_SIZE[other.species].halfLength, other.dx, other.dz, other.speed, false)) who = 'traffic';
      // Behind an articulated truck, it is the trailer's tail that is ahead.
      const t = this.trailerBody(other);
      if (t !== undefined && consider(t.x, t.z, t.halfLength, t.dx, t.dz, other.speed, false)) who = 'traffic';
    }
    return { gap: who === 'person' ? gap : Math.max(0.1, gap), speed, cap, who };
  }

  /**
   * The road a car will drive over the next `LOOKAHEAD` metres, into `path`
   * as x, z pairs every `PATH_STEP` from its middle: round the turn it is
   * making, along its lane, and on into the lane it has chosen next. How
   * many points there are; fewer where its way on is not known yet.
   */
  private pathAhead(car: Car): number {
    const chunk = this.network.get(car.spot.chunk);
    const lane = chunk?.lanes[car.spot.lane];
    if (chunk === undefined || lane === undefined) return 0;
    const ox = chunk.group.position.x, oz = chunk.group.position.z;
    let count = 0;
    const max = path.length / 2;
    pathBox[0] = pathBox[1] = Infinity;
    pathBox[2] = pathBox[3] = -Infinity;
    const turn = car.turn;
    for (let s = 0; count < max; s += PATH_STEP) {
      let on = lane, at = chunk, d: number;
      if (turn !== undefined && turn.s + s < turn.curve.length) {
        sampleTurn(turn.curve, turn.s + s, ahead);
        path[count * 2] = ahead.x + ox;
        path[count * 2 + 1] = ahead.z + oz;
        count++;
        continue;
      }
      d = turn !== undefined ? turn.end + (turn.s + s - turn.curve.length) : car.spot.distance + s;
      if (d > lane.length) {
        const next = car.next === null || car.next === undefined ? undefined : car.next;
        const nextChunk = next === undefined ? undefined : this.network.get(next.chunk);
        const nextLane = next === undefined ? undefined : nextChunk?.lanes[next.lane];
        if (next === undefined || nextChunk === undefined || nextLane === undefined) break;
        // Round the corner it will cut, not out to the junction's middle.
        if (car.planned?.next !== next) {
          sampleLane(lane, lane.length, leaving);
          const plan = this.planTurn(chunk, leaving, next);
          if (plan === undefined) break;
          car.planned = { next, turn: plan };
        }
        const plan = car.planned.turn;
        const past = d - lane.length;
        if (plan !== null && past < plan.curve.length) {
          sampleTurn(plan.curve, past, ahead);
          path[count * 2] = ahead.x + plan.into.group.position.x;
          path[count * 2 + 1] = ahead.z + plan.into.group.position.z;
          count++;
          continue;
        }
        d = plan !== null ? plan.end + (past - plan.curve.length) : next.distance + past;
        if (d > nextLane.length) break;
        on = nextLane;
        at = nextChunk;
      }
      sampleLane(on, d, ahead);
      path[count * 2] = ahead.x + at.group.position.x;
      path[count * 2 + 1] = ahead.z + at.group.position.z;
      count++;
    }
    for (let k = 0; k < count; k++) {
      pathBox[0] = Math.min(pathBox[0], path[k * 2]);
      pathBox[1] = Math.min(pathBox[1], path[k * 2 + 1]);
      pathBox[2] = Math.max(pathBox[2], path[k * 2]);
      pathBox[3] = Math.max(pathBox[3], path[k * 2 + 1]);
    }
    return count;
  }

  /**
   * How far along its own road ahead (`pathAhead`, `count` points of it) a
   * car would reach the point (px, pz), metres, or Infinity if that is not
   * on it within `LOOKAHEAD`.
   */
  private pathTo(car: Car, count: number, px: number, pz: number): number {
    if (Math.hypot(px - car.x, pz - car.z) > LOOKAHEAD + 5) return Infinity;
    const reach = VEHICLE_SIZE[car.species].halfWidth + 1.3;
    for (let k = 1; k < count; k++) {
      if (Math.hypot(path[k * 2] - px, path[k * 2 + 1] - pz) < reach) return k * PATH_STEP;
    }
    return Infinity;
  }

  private drive(car: Car, dt: number, px: number, pz: number): boolean {
    const chunk = this.network.get(car.spot.chunk)!;
    const lane = chunk.lanes[car.spot.lane];

    // Slow for a bend: how far the lane turns over the next stretch.
    sampleLane(lane, car.spot.distance + 15 + car.speed, ahead);
    const turn = ahead.dx * car.dx + ahead.dz * car.dz;
    const bend = 0.35 + 0.65 * Math.max(0, Math.min(1, (turn - 0.5) / 0.48));
    let desired = Math.min(lane.speed * car.temper, TRUCK_TOP[car.species] ?? Infinity) * bend;
    // Responding: blue lights and siren, faster than the road allows.
    if (car.police?.pursuing === true) {
      desired = Math.min(desired * PURSUING_SHARE, desired + PURSUING_OVER);
      // Coming up level with the one it chases, off the road beside it: it pulls up there.
      const c = this.chase;
      if (c !== undefined) {
        const rx = c.x - car.x, rz = c.z - car.z;
        const along = rx * car.dx + rz * car.dz, across = Math.abs(rx * car.dz - rz * car.dx);
        if (across < PULL_UP_ACROSS && along > -6 && along < 20) desired = Math.min(desired, Math.max(0, along - 2) * 0.5);
      }
    }
    else if (car.police?.responding === true) desired = Math.min(desired * RESPONDING_SHARE, desired + RESPONDING_OVER);
    // Giving way to the police behind: over a lane where there is one to go
    // over into, else slowing and pulling over toward the verge.
    if (car.yielding > 0 && lane.right >= 0) {
      car.honked = Math.max(car.honked, 1);
      car.asideWant = 0;
    } else if (car.yielding > 0) {
      desired = Math.min(desired, YIELD_SPEED);
    }
    const driver = isHeavy(car.species) ? TRUCK_DRIVER : car.temperament.impatient ? IMPATIENT_DRIVER : CAR_DRIVER;
    if (car.ring && (car.lastDX !== 0 || car.lastDZ !== 0)) {
      car.roundTurned += Math.acos(Math.max(-1, Math.min(1, car.dx * car.lastDX + car.dz * car.lastDZ)));
    } else if (!car.ring) {
      car.roundTurned = 0;
    }
    // Counted from the first moment on the ring, not from the turn onto it.
    car.lastDX = car.ring ? car.dx : 0;
    car.lastDZ = car.ring ? car.dz : 0;
    if (car.turn !== undefined) {
      // Through the turn at what its curve allows: 3.5 m/s² sideways, a
      // truck 2, high and heavy as it is.
      const t = car.turn;
      const sideways = driver === TRUCK_DRIVER ? 2 : 3.5;
      const through = Math.sqrt(sideways * t.curve.length / Math.max(0.05, t.curve.turn));
      const lead = this.leader(car, px, pz);
      this.brake(car, dt, Math.min(desired, through, lead.cap), lead, driver);
      // Standing in a turn counts toward giving up waiting, as anywhere.
      if (car.speed < 0.3 || car.stuck > STUCK) car.stuck += dt;
      else car.stuck = 0;
      if (car.stuck > STUCK + UNSTICK) car.stuck = 0;
      t.s += car.speed * dt;
      if (t.s >= t.curve.length) {
        car.spot.distance = t.end + (t.s - t.curve.length);
        car.turn = undefined;
      }
      return true;
    }
    if (!car.ring) {
      car.exit = undefined;
      car.passed.clear();
    } else if (car.exit === undefined && car.roundTurned > ROUND_MAYBE) {
      this.lookForExit(car, lane, chunk.group.position.x, chunk.group.position.z);
    }
    if (car.exit !== undefined) {
      // Slowing for the turn off, as for a lane's end.
      sampleLane(this.network.get(car.exit.spot.chunk)?.lanes[car.exit.spot.lane] ?? lane, car.exit.spot.distance + 4, ahead);
      desired = Math.min(desired, turnSpeedLimit(ahead.dx * car.dx + ahead.dz * car.dz, car.exit.at - car.spot.distance));
    }
    const lead = this.leader(car, px, pz);
    const remaining = lane.length - car.spot.distance;
    // A bus on its line: in to its stops, standing its time there, paced to its timetable.
    if (car.bus !== undefined) {
      const step = serve(car.bus, car.x, car.z, car.speed, VEHICLE_SIZE[car.species].halfLength, desired, dt, Date.now());
      desired = Math.min(desired, step.pace);
      const stopGap = toStop(step.stopGap, driver.minGap);
      if (stopGap < lead.gap) { lead.gap = stopGap; lead.speed = 0; lead.who = 'traffic'; }
      car.asideWant = step.kerb && lane.right < 0 ? BUS_KERB : 0;
      car.signalling = step.signal;
      if (step.done) car.bus = undefined;
    }
    car.settled += dt;
    car.honked = Math.max(0, car.honked - dt);
    if (this.pressing(car, px, pz)) car.honked = Math.max(car.honked, 1);
    const ready = car.settled > (car.honked > 0 ? 1.5 : car.temperament.impatient ? SETTLE * 0.6 : SETTLE);
    if (remaining > NO_CHANGE && ready && this.changeLanes(car, lane, desired, lead, px, pz)) {
      // Drive on from the new lane this frame; it has just settled, so it stays there.
      return this.drive(car, dt, px, pz);
    }

    if (remaining < PLAN) {
      // The end of the lane: where it is, which way it runs there, and where next.
      sampleLane(lane, lane.length, ahead);
      const jx = ahead.x + chunk.group.position.x, jz = ahead.z + chunk.group.position.z;
      const endDX = ahead.dx, endDZ = ahead.dz;
      if (car.next !== undefined && car.next !== null && this.network.get(car.next.chunk)?.lanes[car.next.lane] === undefined) {
        car.next = undefined;
      }
      if (car.next === undefined) {
        // How far round the ring it will have gone by the end of this lane.
        const round = car.ring ? car.roundTurned + Math.acos(Math.max(-1, Math.min(1, endDX * car.dx + endDZ * car.dz))) : 0;
        const leave = !car.ring ? undefined : round > ROUND_MUST ? 'must' : round > ROUND_MAYBE ? 'maybe' : 'no';
        // A bus the way its line goes: toward its way a little past the junction.
        const toward = car.police?.pursuing === true ? this.chase
          : car.bus !== undefined ? this.routeAhead(car.bus, remaining + ROUTE_AHEAD) : undefined;
        car.next = this.network.next(jx, jz, endDX, endDZ, car.spot, this.rng, leave, toward) ?? null;
      }
      const nextLane = car.next === null ? undefined : this.network.get(car.next.chunk)!.lanes[car.next.lane];
      if (car.next !== null && nextLane !== undefined) {
        sampleLane(nextLane, car.next.distance + 4, ahead);
        desired = Math.min(desired, turnSpeedLimit(ahead.dx * endDX + ahead.dz * endDZ, remaining));
        const responding = car.police?.responding === true;
        if (responding && remaining < 30 && this.mustWait(car, lane, nextLane, car.next!, jx, jz, endDX, endDZ, px, pz)) {
          // Responding, it does not wait its turn: it slows to a crawl and
          // goes, the others waiting for it (see `mustWait`), unless someone
          // is in the junction already, in its way.
          desired = Math.min(desired, JUNCTION_CREEP);
          if (this.inJunction(car, jx, jz, endDX, endDZ)) {
            const line = Math.max(0.1, remaining - VEHICLE_SIZE[car.species].halfLength - 0.8);
            if (line < lead.gap) { lead.gap = line; lead.speed = 0; lead.who = 'traffic'; }
          }
        } else if (remaining < 30 && car.waited < PATIENCE && this.mustWait(car, lane, nextLane, car.next!, jx, jz, endDX, endDZ, px, pz)) {
          // Its nose short of the lane's end, well short of a roundabout's
          // ring, so cars going by do not pass through it.
          const short = VEHICLE_SIZE[car.species].halfLength + (nextLane.roundabout ? 2.5 : 0.8);
          const line = Math.max(0.1, remaining - short);
          if (line < lead.gap) { lead.gap = line; lead.speed = 0; lead.who = 'traffic'; }
          if (car.speed < 0.5) car.waited += dt;
        }
      }
    }

    if (car.police?.responding === true) desired = this.overtake(car, lead, desired, dt);
    this.brake(car, dt, Math.min(desired, lead.cap), lead, driver);
    car.cause = lead.cap < desired && lead.who !== 'person' ? 'person' : lead.who;
    this.impatience(car, dt, lead);
    if (car.speed < 0.3 || car.stuck > STUCK) car.stuck += dt;
    else car.stuck = 0;
    if (car.stuck > STUCK + UNSTICK) car.stuck = 0;
    car.spot.distance += car.speed * dt;

    if (car.exit !== undefined && car.spot.distance >= car.exit.at) {
      // Off the roundabout, from partway round its ring, glided on from
      // where it has got to this frame (`x`, `z` are where it began it).
      const target = car.exit.spot, over = car.spot.distance - car.exit.at;
      sampleLane(lane, car.exit.at, ahead);
      car.exit = undefined;
      return this.turnOnto(car, chunk, target, over);
    }
    if (car.spot.distance < lane.length) return true;
    const next = car.next ?? this.network.next(car.x + car.dx * car.speed * dt, car.z + car.dz * car.speed * dt, car.dx, car.dz, car.spot, this.rng);
    if (next === undefined || next === null) {
      // A ring mapped as one closed way is one lane ending where it began:
      // round it goes again, and its count of how far round carries on, so
      // it still takes an exit.
      if (!closed(lane)) return false;
      car.spot.distance -= lane.length;
      car.next = undefined;
      car.passed.clear();
      return true;
    }
    const over = car.spot.distance - lane.length;
    sampleLane(lane, lane.length, ahead);
    return this.turnOnto(car, chunk, next, over);
  }

  /**
   * Speed up or slow down by the Intelligent Driver Model, toward `desired`
   * and short of `lead`. For traffic it brakes at most 8 m/s²; for a person,
   * as hard as a car can, and if even that is too late it stops dead rather
   * than drive into them.
   */
  private brake(car: Car, dt: number, desired: number, lead: Lead, driver: typeof CAR_DRIVER): void {
    car.noticed = car.playerInWay ? car.noticed + dt : 0;
    // Having run someone down: stood on the brakes, then standing.
    if (car.struck > 0) {
      car.struck -= dt;
      car.speed = Math.max(0, car.speed - 8 * dt);
      return;
    }
    if (lead.who === 'person' && lead.gap <= 0.3) {
      car.speed = 0;
      return;
    }
    const floor = lead.who === 'person' ? -PERSON_BRAKE : -8;
    const accel = Math.max(floor, idmAcceleration(car.speed, desired, Math.max(0.1, lead.gap), lead.speed, driver));
    car.speed = Math.max(0, car.speed + accel * dt);
  }

  /**
   * Held up by the player standing in its way, a driver waits, then sounds
   * the horn, and again now and then while still held up: a patient one
   * after the best part of ten seconds, an impatient one after three or so.
   */
  private impatience(car: Car, dt: number, lead: Lead): void {
    car.nagged -= dt;
    if (lead.who !== 'player' || car.speed > 0.5 || lead.gap > 12 || lead.speed > 0.5) {
      car.held = Math.max(0, car.held - dt * 2);
      return;
    }
    car.held += dt;
    if (car.held < car.temperament.patience || car.nagged > 0) return;
    car.nagged = car.temperament.nagging;
    this.toots.push(car.x, car.z);
    this.hornsSounded++;
  }

  /**
   * Going round a roundabout, watch for the roads off it just ahead, and
   * decide once at each whether to take it: at even odds, or surely once
   * three quarters of the way round. A ring mapped as one long way has its
   * exits partway along, not at its end.
   */
  private lookForExit(car: Car, lane: Lane, ox: number, oz: number): void {
    const at = car.spot.distance + 10;
    if (at >= lane.length - 2) return;
    sampleLane(lane, at, ahead);
    const spot = this.network.exitNear(ahead.x + ox, ahead.z + oz, ahead.dx, ahead.dz);
    if (spot === undefined) return;
    const key = `${spot.chunk}#${spot.lane}`;
    if (car.passed.has(key)) return;
    car.passed.add(key);
    // How far round it will be by then.
    const round = car.roundTurned + Math.acos(Math.max(-1, Math.min(1, ahead.dx * car.dx + ahead.dz * car.dz)));
    if (round > ROUND_MUST || this.rng() < 0.5) car.exit = { at, spot };
  }

  /**
   * Whether a car should stop at the end of `lane` before going on into
   * `next` at the junction at scene (jx, jz):
   *
   * - Onto a bigger road, it gives way to everything on roads that big.
   * - Where town roads of one size meet, it gives way to traffic from its
   *   right, as at an unmarked Swedish crossing (högerregeln). Bigger roads
   *   have signs and lights instead, which are not modelled.
   * - Always, it does not drive into a car crossing the junction, nor into
   *   one level with where it would join its new lane.
   *
   * Only traffic on the move is waited for. A car standing still is either
   * queueing or waiting to pull out itself, and waiting on it is how a
   * crossing locks up: four cars each yielding to their right, or two
   * merging cars each stopped for the other. Among cars all waiting, the
   * one that has waited longest goes first.
   */
  private mustWait(
    car: Car, lane: Lane, next: Lane, spot: LaneSpot,
    jx: number, jz: number, dx: number, dz: number, px: number, pz: number,
  ): boolean {
    // A police car responding, coming up to this junction from another way:
    // everyone waits for it, whatever their right of way.
    if (this.policeComing(car, jx, jz)) return true;
    // Round a roundabout, and off it, the way is the ring's own.
    if (lane.roundabout) return false;
    if (next.roundabout) return this.ringBusy(car, spot, jx, jz, px, pz);
    if (next.style < lane.style) return this.blocked(car, jx, jz, next.style, px, pz);
    const crossing = next.style === lane.style && lane.style >= ROAD_STYLE.collector;
    for (const other of this.cars) {
      // Traffic on a bridge over the junction, or under it, is no matter.
      if (other === car || other.adrift > 0 || Math.abs(other.y - car.y) > 3) continue;
      const rx = other.x - jx, rz = other.z - jz;
      const facing = other.dx * dx + other.dz * dz;
      // Someone across the junction, in it now: moving, not waiting at their own line.
      if (Math.abs(facing) < 0.7 && other.speed > 0.5 && Math.hypot(rx, rz) < 7) return true;
      // A trailer still crossing it, though the tractor pulling it is through.
      const tb = this.trailerBody(other);
      if (tb !== undefined && other.speed > 0.3 && Math.abs(tb.dx * dx + tb.dz * dz) < 0.85 && clearance(tb, jx, jz) < 6) return true;
      // Someone where it would join the lane: just past the joining point,
      // or coming up to it.
      if (other.spot.chunk === spot.chunk && other.spot.lane === spot.lane) {
        const along = other.spot.distance - spot.distance;
        if (along > -1 && along < 6) return true;
        if (along > -12 && along <= -1 && other.speed > 1) return true;
      }
      // From the right, at a crossing of equals.
      if (crossing) {
        const otherLane = this.network.get(other.spot.chunk)?.lanes[other.spot.lane];
        if (otherLane === undefined || otherLane.style > lane.style || Math.abs(facing) > 0.7) continue;
        const right = rx * -dz + rz * dx;
        const moving = other.speed > 0.5 || (other.waited > 0 && other.waited > car.waited);
        if (moving && right > 2 && comingThrough(rx, rz, other.dx, other.dz, other.speed)) return true;
      }
    }
    return false;
  }

  /**
   * A responding police car held up behind traffic that will not pull over:
   * out into the oncoming lane while it is clear far enough ahead, and back
   * in once there is room. Coming out of it with something oncoming, it
   * drops back behind. Returns the speed it may drive at.
   */
  private overtake(car: Car, lead: Lead, desired: number, dt: number): number {
    const blocked = lead.who === 'traffic' && lead.gap < 25 && lead.speed < desired - 3;
    if (car.passing > 0) {
      if (this.oncomingClear(car, ONCOMING_ABORT)) car.passing = Math.max(0, car.passing - dt);
      else {
        // Something coming: back in, behind whoever it was passing.
        car.passing = 0;
        if (this.besideInLane(car)) desired = Math.min(desired, 3);
      }
    }
    if (blocked && this.oncomingClear(car, ONCOMING_CLEAR) && this.twoWay(car)) car.passing = PASSING;
    // Never back in on top of someone: out until there is room.
    if (car.passing <= 0 && car.aside < -1 && this.besideInLane(car) && this.oncomingClear(car, ONCOMING_ABORT)) car.passing = 0.3;
    if (car.passing > 0) car.asideWant = -ONCOMING_ASIDE;
    return desired;
  }

  /**
   * Whether the oncoming lane beside `car`, from a car length behind to
   * `reach` metres ahead, is clear of anyone coming the other way, or
   * already out in it.
   */
  private oncomingClear(car: Car, reach: number): boolean {
    for (const other of this.cars) {
      if (other === car || Math.abs(other.y - car.y) > 3) continue;
      const rx = other.x + other.asideX - car.x, rz = other.z + other.asideZ - car.z;
      const along = rx * car.dx + rz * car.dz;
      if (along < -8 || along > reach) continue;
      // Left of its heading, (dz, -dx).
      const left = rx * car.dz - rz * car.dx;
      if (left > 1.2 && left < 6.5) return false;
    }
    return true;
  }

  /** Whether the road `car` is on has a lane the other way beside it to pass in: asked once a lane. */
  private twoWay(car: Car): boolean {
    const key = `${car.spot.chunk}:${car.spot.lane}`;
    if (car.oncoming?.key !== key) car.oncoming = { key, yes: this.network.hasOncoming(car.x, car.z, car.dx, car.dz) };
    return car.oncoming.yes;
  }

  /** Whether anyone going its way in the lane it has left is alongside it, or just ahead. */
  private besideInLane(car: Car): boolean {
    for (const other of this.cars) {
      if (other === car || Math.abs(other.y - car.y) > 3 || other.dx * car.dx + other.dz * car.dz < 0.7) continue;
      const rx = other.x - car.x, rz = other.z - car.z;
      const along = rx * car.dx + rz * car.dz;
      if (along > -7 && along < 12 && Math.abs(rx * car.dz - rz * car.dx) < 2) return true;
    }
    return false;
  }

  /**
   * Whether a responding police car is coming up to the junction at scene
   * (jx, jz), within `POLICE_HEARD` of it, from another way than `car`'s:
   * one coming up behind it, the same way, is let by otherwise (see
   * `giveWay`), so the car ahead of it at the line still goes.
   */
  private policeComing(car: Car, jx: number, jz: number): boolean {
    // The player, blue lights on, coming up to it from another way.
    const speed = Math.hypot(this.playerVX, this.playerVZ);
    if (this.playerSiren && speed > 1 && this.level(car)) {
      const pdx = this.playerVX / speed, pdz = this.playerVZ / speed;
      const rx = jx - this.lastPX, rz = jz - this.lastPZ;
      if (pdx * car.dx + pdz * car.dz <= 0.7 && Math.hypot(rx, rz) <= POLICE_HEARD && rx * pdx + rz * pdz >= -4) return true;
    }
    for (const other of this.cars) {
      if (other === car || other.police?.responding !== true || Math.abs(other.y - car.y) > 3) continue;
      if (other.dx * car.dx + other.dz * car.dz > 0.7) continue;
      const rx = jx - other.x, rz = jz - other.z;
      if (Math.hypot(rx, rz) > POLICE_HEARD || rx * other.dx + rz * other.dz < -4) continue;
      return true;
    }
    return false;
  }

  /** Whether anyone is in the junction at scene (jx, jz) now, crossing the way `car` would go through along (dx, dz). */
  private inJunction(car: Car, jx: number, jz: number, dx: number, dz: number): boolean {
    for (const other of this.cars) {
      if (other === car || other.adrift > 0 || Math.abs(other.y - car.y) > 3) continue;
      if (Math.abs(other.dx * dx + other.dz * dz) < 0.7 && other.speed > 0.5 && Math.hypot(other.x - jx, other.z - jz) < 7) return true;
    }
    return false;
  }

  /**
   * Whether a car waiting at the entry to a roundabout at scene (jx, jz)
   * must let traffic on the ring go first: someone going round who would
   * reach the entry within about three seconds, or is in it now, or a car
   * standing where it would join. With a gap before the next car round, it
   * goes, so a ring that is busy but moving still takes cars in.
   */
  private ringBusy(car: Car, spot: LaneSpot, jx: number, jz: number, px: number, pz: number): boolean {
    for (const other of this.cars) {
      if (other === car || other.adrift > 0 || Math.abs(other.y - car.y) > 3) continue;
      if (other.spot.chunk === spot.chunk && other.spot.lane === spot.lane) {
        const along = other.spot.distance - spot.distance;
        if (along > -1 && along < 6) return true;
      }
      if (!other.ring) continue;
      const rx = jx - other.x, rz = jz - other.z;
      const d = Math.hypot(rx, rz);
      if (d < 6) return true;
      // Still to come round to the entry: it lies ahead of the car, not behind.
      if (rx * other.dx + rz * other.dz > 0 && d < 6 + other.speed * 3) return true;
    }
    if (!this.level(car)) return false;
    const speed = Math.hypot(this.playerVX, this.playerVZ);
    if (Math.hypot(px - jx, pz - jz) < 8) return true;
    return speed > 0.5 && comingThrough(px - jx, pz - jz, this.playerVX / speed, this.playerVZ / speed, speed);
  }

  /**
   * Whether anything is coming through the junction at scene (jx, jz) along
   * a road of `style` or bigger: a car on it, or the player, who has right of
   * way whatever they are driving and is never to be pulled out in front of.
   */
  private blocked(car: Car, jx: number, jz: number, style: number, px: number, pz: number): boolean {
    for (const other of this.cars) {
      if (other === car || other.adrift > 0 || Math.abs(other.y - car.y) > 3) continue;
      const otherLane = this.network.get(other.spot.chunk)?.lanes[other.spot.lane];
      if (otherLane === undefined || otherLane.style > style) continue;
      if (comingThrough(other.x - jx, other.z - jz, other.dx, other.dz, other.speed)) return true;
    }
    if (!this.level(car)) return false;
    const speed = Math.hypot(this.playerVX, this.playerVZ);
    if (speed < 0.5) return Math.hypot(px - jx, pz - jz) < 8;
    return comingThrough(px - jx, pz - jz, this.playerVX / speed, this.playerVZ / speed, speed);
  }

  /**
   * Push a body of `radius` at scene (x, z), from `feet` to `head`, out of any
   * car it is inside, through the nearer side. The result and the car it hit
   * (its number, kind and velocity), or undefined if it touched none.
   */
  collide(x: number, z: number, radius: number, feet: number, head: number):
    { x: number; z: number; car: { id: number; species: VehicleSpecies; vx: number; vz: number; police: boolean } } | undefined {
    let px = x, pz = z, hit: Car | undefined;
    // Out of one box, through its nearer side.
    const out = (cx: number, cz: number, yaw: number, halfWidth: number, halfLength: number) => {
      // Into the box's frame: u across, w along.
      const c = Math.cos(yaw), s = Math.sin(yaw);
      const dx = px - cx, dz = pz - cz;
      const u = dx * c - dz * s, w = dx * s + dz * c;
      const hu = halfWidth + radius, hw = halfLength + radius;
      if (Math.abs(u) >= hu || Math.abs(w) >= hw) return false;
      let nu = u, nw = w;
      if (hu - Math.abs(u) < hw - Math.abs(w)) nu = Math.sign(u || 1) * hu;
      else nw = Math.sign(w || 1) * hw;
      px = cx + nu * c + nw * s;
      pz = cz - nu * s + nw * c;
      return true;
    };
    for (const car of this.cars) {
      const size = VEHICLE_SIZE[car.species];
      if (head <= car.y || feet >= car.y + size.height) continue;
      if (out(car.x + car.offsetX + car.asideX, car.z + car.offsetZ + car.asideZ, car.yaw, size.halfWidth, size.halfLength)) hit = car;
      const t = this.trailerBody(car);
      if (t !== undefined && out(t.x, t.z, Math.atan2(-t.dx, -t.dz), t.halfWidth, t.halfLength)) hit = car;
    }
    if (hit === undefined) return undefined;
    return { x: px, z: pz, car: { id: hit.id, species: hit.species, vx: hit.dx * hit.speed, vz: hit.dz * hit.speed, police: hit.police !== undefined } };
  }

  /**
   * Take a car out of the traffic, hit and no longer driven: where it is
   * (scene metres), which way it faces, how fast it was going, and what it
   * is. The game makes a wreck of it. Undefined if it has already gone.
   */
  release(id: number): Released | undefined {
    const i = this.cars.findIndex(c => c.id === id);
    if (i < 0) return undefined;
    const [car] = this.cars.splice(i, 1);
    return this.released(car);
  }

  private released(car: Car): Released {
    return {
      species: car.species, paint: car.paint, x: car.x + car.offsetX + car.asideX, y: car.y, z: car.z + car.offsetZ + car.asideZ, yaw: car.yaw,
      vx: car.dx * car.speed, vz: car.dz * car.speed,
      trailerYaw: car.trailer === undefined ? undefined : Math.atan2(-car.trailer.dx, -car.trailer.dz),
      police: car.police === undefined ? undefined : { responding: car.police.responding },
    };
  }

  /** Crashes since last asked: see `crashes`. */
  takeCrashes(): TrafficCrash[] {
    return this.crashed.splice(0, this.crashed.length);
  }

  /**
   * Find the traffic running into itself, or into a wreck: two bodies (a
   * car, a truck's tractor or its trailer) overlapping by more than
   * `CRASH_DEPTH` while closing faster than `CRASH_SPEED`. Both are taken out
   * of the traffic, for the game to make wrecks of, with the line of the
   * hit and where. Only near the player (`CRASH_RANGE`): further off nobody
   * would see it. A car pushing through, its last resort after standing for
   * ever, is edging, not crashing; slower than that is a nudge.
   */
  private crashes(px: number, pz: number): void {
    const bodies: Array<{ car: Car; f: Footprint; trailer: boolean }> = [];
    for (const car of this.cars) {
      if (Math.abs(car.x - px) > CRASH_RANGE || Math.abs(car.z - pz) > CRASH_RANGE || car.adrift > 0) continue;
      bodies.push({ car, f: this.footprint(car), trailer: false });
      const t = this.trailerBody(car);
      if (t !== undefined) bodies.push({ car, f: t, trailer: true });
    }
    const gone = new Set<Car>();
    const velocity = (car: Car, trailer: boolean): [number, number] => {
      const t = trailer ? car.trailer : undefined;
      return t !== undefined ? [t.dx * car.speed, t.dz * car.speed] : [car.dx * car.speed, car.dz * car.speed];
    };
    for (let i = 0; i < bodies.length; i++) {
      const A = bodies[i];
      if (gone.has(A.car)) continue;
      for (let j = i + 1; j < bodies.length; j++) {
        const B = bodies[j];
        if (B.car === A.car || gone.has(B.car) || Math.abs(A.car.y - B.car.y) > 3) continue;
        if (Math.abs(A.f.x - B.f.x) > 20 || Math.abs(A.f.z - B.f.z) > 20) continue;
        if (A.car.stuck > STUCK || B.car.stuck > STUCK) continue;
        const hit = overlap(A.f, B.f);
        if (hit === undefined || hit.depth < CRASH_DEPTH) continue;
        const [ax, az] = velocity(A.car, A.trailer), [bx, bz] = velocity(B.car, B.trailer);
        const closing = (ax - bx) * hit.nx + (az - bz) * hit.nz;
        if (closing < CRASH_SPEED || !damaging(A.car.species, B.car.species, closing)) continue;
        gone.add(A.car).add(B.car);
        this.crashed.push({ a: this.released(A.car), b: this.released(B.car), nx: hit.nx, nz: hit.nz, x: hit.x, z: hit.z, closing });
        this.crashCount++;
        break;
      }
      if (gone.has(A.car) || A.car.stuck > STUCK) continue;
      // Into a wreck, or a car left standing in the road.
      for (const [k, o] of this.obstacles.entries()) {
        if (Math.abs(A.f.x - o.x) > 20 || Math.abs(A.f.z - o.z) > 20) continue;
        const hit = overlap(o, A.f);
        if (hit === undefined || hit.depth < CRASH_DEPTH) continue;
        const [ax, az] = velocity(A.car, A.trailer);
        // Closing on it: moving against the normal out of it.
        const closing = -(ax * hit.nx + az * hit.nz);
        if (closing < CRASH_SPEED || !damaging(A.car.species, o.species ?? 'carSedan', closing)) continue;
        gone.add(A.car);
        this.crashed.push({ a: this.released(A.car), obstacle: k, nx: -hit.nx, nz: -hit.nz, x: hit.x, z: hit.z, closing });
        this.crashCount++;
        break;
      }
    }
    if (gone.size > 0) for (let i = this.cars.length - 1; i >= 0; i--) if (gone.has(this.cars[i])) this.cars.splice(i, 1);
  }

  /**
   * Vehicles standing where traffic must not drive into them: wrecks, and
   * the cars the player has left. Scene x, z of each middle, which way it
   * faces, and its half-length.
   */
  setObstacles(boxes: Array<Footprint & { species?: VehicleSpecies }>): void {
    this.obstacles = boxes;
  }

  /** A car's body as a box, where it is drawn. */
  private footprint(car: Car): Footprint {
    const size = VEHICLE_SIZE[car.species];
    return { x: car.x + car.offsetX + car.asideX, z: car.z + car.offsetZ + car.asideZ, dx: -Math.sin(car.yaw), dz: -Math.cos(car.yaw), halfLength: size.halfLength, halfWidth: size.halfWidth };
  }

  /** Where an articulated truck's trailer is, as a box: its middle, which way it points, its half-length and half-width. */
  private trailerBody(car: Car): { x: number; z: number; dx: number; dz: number; halfLength: number; halfWidth: number } | undefined {
    const t = car.trailer;
    if (t === undefined) return undefined;
    const spec = this.models.get(car.species)!.trailer!;
    const fx = -Math.sin(car.yaw), fz = -Math.cos(car.yaw);
    const kx = car.x + car.offsetX + car.asideX - fx * spec.kingpinZ, kz = car.z + car.offsetZ + car.asideZ - fz * spec.kingpinZ;
    // The box runs back from a little ahead of the kingpin to the tail.
    const front = -1.2, mid = (front + spec.length) / 2;
    return { x: kx - t.dx * mid, z: kz - t.dz * mid, dx: t.dx, dz: t.dz, halfLength: (spec.length - front) / 2, halfWidth: spec.width / 2 };
  }

  /**
   * Keep right, pass on the left: move out a lane when stuck behind someone
   * slower with room to the left, and back in when the lane to the right has
   * room and nobody slow in it. True if the car changed lanes.
   */
  private changeLanes(car: Car, lane: Lane, desired: number, lead: Lead, px: number, pz: number): boolean {
    const chunk = this.network.get(car.spot.chunk)!;
    // Someone close behind wanting by, or a horn: over to the right, room
    // allowing, whatever is ahead there.
    if (car.honked > 0 && lane.right >= 0) {
      const target = chunk.lanes[lane.right];
      if (this.room(car, lane, lane.right, target, px, pz).clear) return this.moveTo(car, lane, lane.right, target);
      return false;
    }
    // An impatient driver pulls out to pass for less.
    const slowed = lead.gap < 45 && lead.speed < desired - (car.temperament.impatient ? 1.2 : 2.5) && desired > 8;
    // A truck keeps to the right-hand lane; it does not pull out to pass.
    if (slowed && lane.left >= 0 && !isHeavy(car.species)) {
      const target = chunk.lanes[lane.left];
      if (this.room(car, lane, lane.left, target, px, pz).clear) return this.moveTo(car, lane, lane.left, target);
    }
    if (!slowed && lane.right >= 0) {
      const target = chunk.lanes[lane.right];
      const room = this.room(car, lane, lane.right, target, px, pz);
      // Not back in just to come up behind someone slower and pull out again.
      if (room.clear && (room.aheadGap > 80 || room.aheadSpeed > desired - 1.5)) return this.moveTo(car, lane, lane.right, target);
    }
    return false;
  }

  /**
   * Whether the player is close behind a car in its lane and coming on
   * faster or as fast: tailgating it in the overtaking lane, as a driver
   * would read it.
   */
  private pressing(car: Car, px: number, pz: number): boolean {
    const speed = Math.hypot(this.playerVX, this.playerVZ);
    if (speed < 8 || !this.level(car)) return false;
    const rx = px - car.x, rz = pz - car.z;
    const along = rx * car.dx + rz * car.dz;
    if (along > -2 || along < -30) return false;
    if (Math.abs(rx * car.dz - rz * car.dx) > 1.8) return false;
    return (this.playerVX * car.dx + this.playerVZ * car.dz) > car.speed - 1;
  }

  /**
   * The player sounds the horn from scene (px, pz), facing (hx, hz). Cars
   * just ahead in the way move over to the right where they can, and one
   * waiting at a junction stops waiting. None drives into anything for it:
   * the gap to what is ahead still holds. Returns how many heard it.
   */
  honk(px: number, pz: number, hx: number, hz: number): number {
    let heard = 0;
    for (const car of this.cars) {
      const rx = car.x - px, rz = car.z - pz;
      const along = rx * hx + rz * hz;
      if (along < 0 || along > 50 || Math.abs(rx * hz - rz * hx) > 5 || !this.level(car)) continue;
      car.honked = 4;
      heard++;
      // Waiting to pull out, it goes, if the way is clear enough to drive into.
      if (car.speed < 1) car.waited = Math.max(car.waited, PATIENCE);
    }
    return heard;
  }

  /** Whether lane `index` has a gap for the car alongside, the player included, and what is ahead of it there. */
  private room(
    car: Car, lane: Lane, index: number, target: Lane, px: number, pz: number,
  ): { clear: boolean; aheadGap: number; aheadSpeed: number } {
    const at = this.alongside(car, lane, target);
    const own = VEHICLE_SIZE[car.species].halfLength;
    let clear = true, aheadGap = Infinity, aheadSpeed = Infinity;
    // Never across into the player.
    const chunk = this.network.get(car.spot.chunk)!;
    sampleLane(target, at, ahead);
    const rx = px - (ahead.x + chunk.group.position.x), rz = pz - (ahead.z + chunk.group.position.z);
    if (this.level(car) && Math.abs(rx * ahead.dz - rz * ahead.dx) < 2.2) {
      const along = rx * ahead.dx + rz * ahead.dz;
      const playerSpeed = this.playerVX * ahead.dx + this.playerVZ * ahead.dz;
      const gap = Math.abs(along) - own - 2.3;
      if (along >= 0 ? gap < 8 + Math.max(0, car.speed - playerSpeed) * 2.5 : gap < 6 + Math.max(0, playerSpeed - car.speed) * 3) clear = false;
    }
    // An articulated truck reaches far behind its tractor's middle.
    const behind = (c: Car) => (c.trailer !== undefined ? SEMI_REACH : VEHICLE_SIZE[c.species].halfLength);
    const ownBehind = behind(car);
    for (const other of this.cars) {
      if (other === car || other.spot.chunk !== car.spot.chunk || other.spot.lane !== index) continue;
      const ahead = other.spot.distance >= at;
      const gap = Math.abs(other.spot.distance - at) - (ahead ? own + behind(other) : ownBehind + VEHICLE_SIZE[other.species].halfLength);
      if (ahead) {
        if (gap < 8 + Math.max(0, car.speed - other.speed) * 2.5) clear = false;
        if (gap < aheadGap) { aheadGap = gap; aheadSpeed = other.speed; }
      } else if (gap < 6 + Math.max(0, other.speed - car.speed) * 3) {
        clear = false;
      }
    }
    return { clear, aheadGap, aheadSpeed };
  }

  /** Where alongside a car is on the lane next to it, by projecting it across rather than by the ratio of their lengths. */
  private alongside(car: Car, lane: Lane, target: Lane): number {
    const chunk = this.network.get(car.spot.chunk)!;
    const at = projectOnLane(target, car.x - chunk.group.position.x, car.z - chunk.group.position.z, 8, projection);
    return at?.distance ?? car.spot.distance * (target.length / lane.length);
  }

  private moveTo(car: Car, lane: Lane, index: number, target: Lane): boolean {
    const fromX = car.x + car.offsetX, fromZ = car.z + car.offsetZ, fromYaw = car.yaw;
    car.spot = { chunk: car.spot.chunk, lane: index, distance: this.alongside(car, lane, target) };
    car.next = undefined;
    car.waited = 0;
    car.settled = 0;
    if (!this.locate(car)) return false;
    // Across a lane over a few seconds at speed, as a driver does; quicker in town.
    this.ease(car, fromX, fromZ, fromYaw, Math.max(1.5, Math.min(3.2, car.speed / 8)));
    return true;
  }

  /**
   * From the end of `chunk`'s lane (its point and heading in `ahead`), on
   * onto `target`, `over` metres already driven past: along a curve to a
   * point a little way along the new lane, unless that lane simply carries
   * straight on. False if the new lane has gone.
   */
  private turnOnto(car: Car, chunk: ChunkLanes, target: LaneSpot, over: number): boolean {
    car.next = undefined;
    car.waited = 0;
    car.turn = undefined;
    leaving.x = ahead.x; leaving.y = ahead.y; leaving.z = ahead.z; leaving.dx = ahead.dx; leaving.dz = ahead.dz;
    const plan = this.planTurn(chunk, leaving, target);
    if (plan === undefined) return false;
    if (plan === null) {
      // Straight on, where the map only split the road.
      car.spot = { ...target, distance: target.distance + over };
      return this.locate(car);
    }
    car.spot = { ...target, distance: plan.end };
    car.turn = { curve: plan.curve, s: over, end: plan.end };
    return this.locate(car);
  }

  /**
   * The way from the end of `chunk`'s lane, at `from` in its frame, onto
   * `target`: a curve to a point a little way along the new lane, in that
   * lane's chunk's frame, and how far along it comes out. Null where the
   * new lane simply carries straight on; undefined if it has gone.
   */
  private planTurn(chunk: ChunkLanes, from: LaneSample, target: LaneSpot): { curve: TurnCurve; end: number; into: ChunkLanes } | null | undefined {
    const into = this.network.get(target.chunk);
    const lane = into?.lanes[target.lane];
    if (into === undefined || lane === undefined) return undefined;
    // Where it leaves, in the new lane's chunk's frame.
    const x0 = from.x + chunk.group.position.x - into.group.position.x;
    const z0 = from.z + chunk.group.position.z - into.group.position.z;
    const y0 = from.y + chunk.group.position.y - into.group.position.y;
    sampleLane(lane, target.distance, ahead);
    const gap = Math.hypot(ahead.x - x0, ahead.z - z0);
    const bend = Math.acos(Math.max(-1, Math.min(1, ahead.dx * from.dx + ahead.dz * from.dz)));
    if (gap < 0.4 && bend < 0.06) return null;
    // Come out a little way along: further the wider the turn and the gap.
    const end = Math.min(lane.length - 0.5, target.distance + Math.max(3, Math.min(14, 2 + gap * 1.2 + bend * 5)));
    sampleLane(lane, end, ahead);
    return { curve: makeTurn(x0, z0, y0, from.dx, from.dz, ahead.x, ahead.z, ahead.y, ahead.dx, ahead.dz), end, into };
  }

  private draw(dt: number): void {
    const counts = new Map<InstancedMesh, number>();
    let left = 0, right = 0, trailerCount = 0;
    this.policeClock += dt;
    this.policeLights.begin();
    this.carLights.begin(floraNight.value);
    for (const car of this.cars) {
      // Drawn where it is now, after this frame's driving, not where it began it.
      if (car.adrift === 0) this.locate(car);
      // How fast the glide is carrying it across, m/s, for its heading.
      let glideX = 0, glideZ = 0;
      const g = car.glide;
      if (g !== undefined) {
        g.t += dt;
        const u = Math.min(1, g.t / g.seconds);
        const remain = 1 - u * u * (3 - 2 * u);
        car.offsetX = g.x * remain;
        car.offsetZ = g.z * remain;
        car.offsetYaw = g.yaw * remain;
        const rate = (6 * u * (1 - u)) / g.seconds;
        glideX = -g.x * rate;
        glideZ = -g.z * rate;
        if (u >= 1) car.glide = undefined;
      }
      // Pulled over, or out to pass: eased across, a metre in a second or so.
      car.aside += (car.asideWant - car.aside) * Math.min(1, dt * 1.2);
      // Right of its heading, (-sin yaw, -cos yaw), is (cos yaw, -sin yaw).
      car.asideX = Math.cos(car.yaw) * car.aside;
      car.asideZ = -Math.sin(car.yaw) * car.aside;
      // Steered along the way it is really moving: gliding across to a new
      // lane, it angles over rather than sliding sideways.
      const vx = car.dx * car.speed + glideX, vz = car.dz * car.speed + glideZ;
      const heading = car.speed > 2 ? Math.atan2(-vx, -vz) : Math.atan2(-car.dx, -car.dz);
      // Turned toward its heading, closely: its lanes are rounded and its
      // turns are curves already, and a body lagging its path slides.
      const before = car.yaw;
      car.yaw += wrapAngle(heading + car.offsetYaw - car.yaw) * Math.min(1, dt * 22);
      const model = this.models.get(car.species)!;
      // The front wheels steer as a car's do through a turn of that rate:
      // tan(steer) = wheelbase × yaw rate / speed.
      const wheelbase = model.wheels.length >= 3 ? Math.abs(model.wheels[0].z - model.wheels[2].z) : 1.5;
      const yawRate = dt > 0 ? wrapAngle(car.yaw - before) / dt : 0;
      const steerTo = car.speed > 0.5 ? Math.atan((wheelbase * yawRate) / car.speed) : car.steer;
      car.steer += (Math.max(-STEER_LOCK, Math.min(STEER_LOCK, steerTo)) - car.steer) * Math.min(1, dt * 6);
      // Rolling forward, toward -z, turns a wheel's top forward: negative about x.
      car.wheelSpin = (car.wheelSpin - (car.speed * dt) / model.wheelRadius) % (Math.PI * 2);

      const mesh = car.police !== undefined ? this.policeMeshes.get(car.species)! : this.meshes.get(car.species)!;
      const k = counts.get(mesh) ?? 0;
      counts.set(mesh, k + 1);
      position.set(car.x + car.offsetX + car.asideX, car.y, car.z + car.offsetZ + car.asideZ);
      // A motorbike leans into its turn, as far as balances it: atan(v·ω/g).
      if (car.species === 'parkedMoto') {
        const want = Math.max(-0.7, Math.min(0.7, Math.atan((car.speed * yawRate) / 9.81)));
        car.lean += (want - car.lean) * Math.min(1, dt * 6);
      }
      euler.set(Math.atan(car.grade), car.yaw, car.lean, 'YXZ');
      rotation.setFromEuler(euler);
      matrix.compose(position, rotation, unit);
      mesh.setMatrixAt(k, matrix);
      mesh.setColorAt(k, colour.setHex(car.paint));
      const roof = ROOF[car.species];
      if (car.police !== undefined && roof !== undefined) {
        const width = BAR_WIDTH[car.species];
        this.policeLights.place(width === undefined ? matrix : barMatrix.copy(matrix).multiply(barScale.makeScale(width, 1, 1)), roof.y, roof.z, car.police.responding, this.policeClock, car.police.phase,
          Math.hypot(car.x - this.lastPX, car.z - this.lastPZ), floraNight.value);
      }

      if (car.trailer !== undefined && model.trailer !== undefined) {
        // The trailer, pulled along behind the kingpin: its axles stay where
        // they were but for being drawn toward it, at their own distance, so
        // in a turn it cuts the corner and comes straight again after, as a
        // real one does (a tractrix).
        const tr = model.trailer, t = car.trailer;
        const fx = -Math.sin(car.yaw), fz = -Math.cos(car.yaw);
        const px = car.x + car.offsetX + car.asideX, pz = car.z + car.offsetZ + car.asideZ;
        const kx = px - fx * tr.kingpinZ, kz = pz - fz * tr.kingpinZ;
        // A jump — the world's origin moved — takes the track along with it.
        if (Math.hypot(kx - t.kx, kz - t.kz) > 6) shiftTrail(t.trail, kx - t.kx, kz - t.kz);
        t.kx = kx;
        t.kz = kz;
        const axle = followTrail(t.trail, kx, kz, tr.axleZ, -fx, -fz);
        let ux = kx - axle.x, uz = kz - axle.z;
        const ul = Math.hypot(ux, uz) || 1;
        ux /= ul; uz /= ul;
        // Never further round on the kingpin than a trailer swings.
        const hitch = wrapAngle(Math.atan2(-ux, -uz) - car.yaw);
        if (Math.abs(hitch) > TRAILER_SWING) {
          const a = car.yaw + Math.sign(hitch) * TRAILER_SWING;
          ux = -Math.sin(a);
          uz = -Math.cos(a);
        }
        t.dx = ux;
        t.dz = uz;
        position.set(kx, car.y, kz);
        euler.set(Math.atan(car.grade), Math.atan2(-ux, -uz), 0, 'YXZ');
        rotation.setFromEuler(euler);
        trailerMatrix.compose(position, rotation, unit);
        this.trailers.setMatrixAt(trailerCount, trailerMatrix);
        this.trailers.setColorAt(trailerCount++, colour.setHex(car.paint));
        this.carLights.place(matrix, this.lamps.get(car.species)!, car.id, car.speed, dt,
          { body: trailerMatrix, layout: this.trailerLamps });
        const size = tr.wheelRadius / this.wheelRadius;
        wheelScale.set(size, size, size);
        for (const wheel of tr.wheels) {
          wheelPosition.set(wheel.x, tr.wheelRadius, wheel.z);
          wheelEuler.set((car.wheelSpin * model.wheelRadius) / tr.wheelRadius, 0, 0, 'YXZ');
          wheelRotation.setFromEuler(wheelEuler);
          wheelMatrix.compose(wheelPosition, wheelRotation, wheelScale).premultiply(trailerMatrix);
          if (wheel.x < 0) this.wheelsLeft.setMatrixAt(left++, wheelMatrix);
          else this.wheelsRight.setMatrixAt(right++, wheelMatrix);
        }
      } else this.carLights.place(matrix, this.lamps.get(car.species)!, car.id, car.speed, dt);

      const size = model.wheelRadius / this.wheelRadius;
      wheelScale.set(size, size, size);
      for (const wheel of model.wheels) {
        wheelPosition.set(wheel.x, model.wheelRadius, wheel.z);
        wheelEuler.set(car.wheelSpin, wheel.front ? car.steer : 0, 0, 'YXZ');
        wheelRotation.setFromEuler(wheelEuler);
        wheelMatrix.compose(wheelPosition, wheelRotation, wheelScale).premultiply(matrix);
        if (wheel.x < 0) this.wheelsLeft.setMatrixAt(left++, wheelMatrix);
        else this.wheelsRight.setMatrixAt(right++, wheelMatrix);
      }
    }
    for (const mesh of [...this.meshes.values(), ...this.policeMeshes.values()]) {
      mesh.count = counts.get(mesh) ?? 0;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor !== null) mesh.instanceColor.needsUpdate = true;
    }
    this.policeLights.commit();
    this.carLights.commit();
    this.trailers.count = trailerCount;
    this.trailers.instanceMatrix.needsUpdate = true;
    if (this.trailers.instanceColor !== null) this.trailers.instanceColor.needsUpdate = true;
    this.wheelsLeft.count = left;
    this.wheelsRight.count = right;
    this.wheelsLeft.instanceMatrix.needsUpdate = true;
    this.wheelsRight.instanceMatrix.needsUpdate = true;
  }

  /** A car of a kind on `spot`, not yet placed: its driver, its paint, at a standstill. */
  private newCar(species: VehicleSpecies, spot: LaneSpot, police: boolean, forced: boolean): Car {
    const truck = isHeavy(species);
    const temperament = pickTemperament(truck ? 1 : this.rng(), this.rng());
    return {
      species,
      paint: police ? 0xffffff : truck ? CAB_PAINTS[Math.floor(this.rng() * CAB_PAINTS.length)] : pickPaint(this.rng()),
      spot,
      speed: 0,
      // A share of the road's speed: an impatient driver a little over it.
      temper: truck ? 0.96 + this.rng() * 0.06 : temperament.impatient ? 1.02 + this.rng() * 0.1 : 0.9 + this.rng() * 0.16,
      temperament,
      held: 0, nagged: 0, cause: 'traffic', planned: undefined,
      noticed: 0, playerInWay: false, reaction: REACTION[0] + this.rng() * (REACTION[1] - REACTION[0]), struck: 0,
      police: police ? { responding: forced, phase: this.rng(), pursuing: forced } : undefined,
      yielding: 0, passing: 0, aside: 0, asideWant: 0, asideX: 0, asideZ: 0, lean: 0,
      x: 0, y: 0, z: 0, dx: 0, dz: 1, grade: 0,
      offsetX: 0, offsetZ: 0, offsetYaw: 0, yaw: 0, adrift: 0, next: undefined, waited: 0,
      stuck: 0, id: this.serial++, settled: 0, ring: false, roundTurned: 0, lastDX: 0, lastDZ: 0,
      exit: undefined, passed: new Set(), wheelSpin: 0, steer: 0, honked: 0, glide: undefined, turn: undefined,
    };
  }

  private spawn(px: number, pz: number): void {
    // The first fill may put cars anywhere but on the player; after that, new
    // ones come in from far off.
    const min = this.populated ? this.opts.spawnMin : 25;
    let budget = this.populated ? 2 : this.wanted;
    while ((this.cars.length - this.busCount() < this.wanted || this.forcePolice > 0) && budget-- > 0) {
      const spot = this.network.pick(px, pz, min, this.opts.spawnMax, this.rng);
      if (spot === undefined) return;
      // A truck, as often as they are on a road like this; else a car.
      const share = TRUCK_SHARE[this.network.get(spot.chunk)!.lanes[spot.lane].style];
      const roll = this.rng();
      // Now and then a police car, more often in a city: a saloon or an estate.
      // Sent in to a chase, or now and then one on patrol.
      const forced = this.forcePolice > 0;
      const police = forced || this.rng() < policeShare(this.roads);
      const bike = police && !forced && this.roads > BIKE_ROADS && this.rng() < BIKE_SHARE;
      const species: VehicleSpecies = bike ? 'parkedMoto' : police ? (this.rng() < 0.55 ? 'carSedan' : 'carEstate')
        : share !== undefined && roll < share.semi ? 'truckSemi'
          : share !== undefined && roll < share.semi + share.box ? 'truckBox' : pickCar(this.rng());
      const truck = isTruckSpecies(species);
      const car = this.newCar(species, spot, police, forced);
      // Most keep right.
      for (let lane = this.network.get(spot.chunk)!.lanes[spot.lane]; lane.right >= 0 && this.rng() < 0.75;) {
        const right = this.network.get(spot.chunk)!.lanes[lane.right];
        spot.distance *= right.length / lane.length;
        spot.lane = lane.right;
        lane = right;
      }
      if (!this.locate(car)) continue;
      // Never on top of another car, nor a truck on a trailer.
      if (this.cars.some(o => Math.hypot(o.x - car.x, o.z - car.z) < (truck || o.trailer !== undefined ? 32 : 14))) continue;
      const lane = this.network.get(spot.chunk)!.lanes[spot.lane];
      car.speed = Math.min(lane.speed * car.temper, TRUCK_TOP[species] ?? Infinity) * 0.8;
      car.yaw = Math.atan2(-car.dx, -car.dz);
      // A trailer starts straight behind its tractor.
      const trailer = this.models.get(species)!.trailer;
      if (trailer !== undefined) {
        const kx = car.x - car.dx * trailer.kingpinZ, kz = car.z - car.dz * trailer.kingpinZ;
        car.trailer = { trail: [kx - car.dx * trailer.axleZ * 1.05, kz - car.dz * trailer.axleZ * 1.05, kx, kz], kx, kz, dx: car.dx, dz: car.dz };
      }
      this.cars.push(car);
      if (forced) this.forcePolice--;
    }
    if (this.cars.length > 0) this.populated = true;
  }

  /**
   * The buses about, each trip laid out in scene x, z (`layoutTrip`). Called
   * as the timetable is fetched, and again whenever the origin moves: a bus
   * out on a trip keeps going, on its new layout.
   */
  setTransit(trips: Array<{ trip: TransitTrip; layout: TripLayout }>): void {
    this.transit = trips;
    const byId = new Map(trips.map(t => [t.trip.id, t]));
    for (const car of this.cars) {
      if (car.bus === undefined) continue;
      const fresh = byId.get(car.bus.trip.id);
      if (fresh === undefined) continue;
      car.bus.trip = fresh.trip;
      car.bus.layout = fresh.layout;
    }
  }

  /** The trams about, a box a section: see `Mover`. */
  setMovers(movers: Mover[]): void {
    this.movers = movers;
  }

  /** The buses out: where each is, which way, its line and where it is going, and whether it is signalling. */
  buses(): Array<{ id: number; x: number; y: number; z: number; yaw: number; grade: number; species: VehicleSpecies; trip: TransitTrip; signalling: boolean }> {
    const out: Array<{ id: number; x: number; y: number; z: number; yaw: number; grade: number; species: VehicleSpecies; trip: TransitTrip; signalling: boolean }> = [];
    for (const car of this.cars) {
      if (car.bus === undefined) continue;
      out.push({
        id: car.id, x: car.x + car.offsetX + car.asideX, y: car.y, z: car.z + car.offsetZ + car.asideZ, yaw: car.yaw, grade: car.grade,
        species: car.species, trip: car.bus.trip, signalling: car.signalling === true,
      });
    }
    return out;
  }

  /** A point a bus's way reaches `ahead` metres on from where it is. */
  private routeAhead(bus: BusService, ahead: number): { x: number; z: number } {
    const p = pointAlong(bus.layout, bus.along + ahead);
    return { x: p.x, z: p.z };
  }

  /**
   * Out onto its road, each bus the timetable has about the player now and
   * that is not out yet: where its trip has it, on the lane there running its
   * way. Once the first are out, not within sight of the player.
   */
  private spawnBuses(px: number, pz: number): void {
    if (this.transit.length === 0) return;
    const out = new Set<string>();
    let count = 0;
    for (const car of this.cars) if (car.bus !== undefined) { out.add(car.bus.trip.id); count++; }
    const now = Date.now();
    const min = this.busesOut ? BUS_SPAWN_MIN : 30;
    for (const { trip, layout } of this.transit) {
      if (count >= MAX_BUSES) break;
      if (out.has(trip.id)) continue;
      const when = scheduledAt(trip, layout, now);
      if (when.after || (when.before && trip.stops[0].depart - now > 60_000)) continue;
      const p = pointAlong(layout, when.along);
      const d = Math.hypot(p.x - px, p.z - pz);
      if (d < min || d > this.opts.spawnMax) continue;
      const spot = this.network.next(p.x, p.z, p.dx, p.dz, undefined, this.rng);
      if (spot === undefined) continue;
      const species = BUS_SPECIES[trip.line.look];
      const car = this.newCar(species, spot, false, false);
      car.paint = species === 'busCity' ? parseInt(trip.line.background.slice(1), 16) : VT_BLUE;
      car.temper = 1;
      if (!this.locate(car)) continue;
      if (Math.hypot(car.x - p.x, car.z - p.z) > BUS_LANE_REACH + 2) continue;
      if (this.cars.some(o => Math.hypot(o.x - car.x, o.z - car.z) < 20)) continue;
      car.bus = startService(trip, layout, when.along);
      // Standing at a stop it is due at, it starts there; else on its way.
      car.speed = when.at >= 0 ? 0 : Math.min(this.network.get(spot.chunk)!.lanes[spot.lane].speed, TRUCK_TOP[species] ?? Infinity) * 0.7;
      car.yaw = Math.atan2(-car.dx, -car.dz);
      this.cars.push(car);
      out.add(trip.id);
      count++;
    }
    if (count > 0) this.busesOut = true;
  }

  /** How many of the cars are buses on their lines, which the hour's count of cars leaves out. */
  private busCount(): number {
    let n = 0;
    for (const car of this.cars) if (car.bus !== undefined) n++;
    return n;
  }

  /** Too many for the hour: let the furthest go, out of sight. */
  private thin(px: number, pz: number): void {
    if (this.cars.length - this.pursuing - this.busCount() <= this.wanted + 2) return;
    let far = -1, farD = this.opts.spawnMin;
    this.cars.forEach((car, i) => {
      // Not one sent in to a chase, nor a bus on its line.
      if (car.police?.pursuing === true || car.bus !== undefined) return;
      const d = Math.hypot(car.x - px, car.z - pz);
      if (d > farD) { farD = d; far = i; }
    });
    if (far >= 0) this.cars.splice(far, 1);
  }
}
